import { invoiceSaveErrorKey } from '../utils/invoice-save';
import CommentsPanel from './comments/CommentsPanel';
import useCommentDraftGuard from './comments/useCommentDraftGuard';
import { hasExpenseCommentIdentity, legacyCommentsText } from '../utils/comments-api';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import axios from "axios";
import closeIcon2 from '../assets/img/icon-close2.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AsyncSelect from 'react-select/async';
import {
    faAngleLeft,
    faAngleRight,
    faEllipsisH,
    faPlusSquare,
    faSpinner,
    faExclamationTriangle,
    faWindowClose
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

import FormTextAreaAutoSaved from './form-text-area-auto-saved';
import FormTextField from './form-textfield';
import CurrencySelect from './currency-select';
import Modal from './modal';
import Checkbox from "../components/checkbox";
import Utils from "../utils/utils";

import { fetch } from '../utils/api-wrapper';
import { loadXpensCategories, getXpensCategoryLabel } from '../utils/xpens-categories';
import { buildAttachmentsListUrl, resolveAttachmentsCount } from '../utils/attachments';
import { resolveDefaultTaxCode, resolveTaxedCode } from '../utils/tax-code-resolvers';
import { getLineItemTableTitle } from '../utils/line-item-table-title';
import { downloadFile } from '../utils/file-manager';
import Switch from "react-switch";
import PdfImageDisplay from "./pdf-image-display";
import PDFVisualizer from './pdf-visualizer.jsx';
import ExpenseFormItem from "./expense-form-item";
import AutoCompleteField from './field-auto-complete';
import { expandMatterNumber, formatMatterLabelFromData, formatMatterNumber } from '../utils/matter-display';
import HoverInfoCard from "./hover-card";
import { cachedFetch } from '../utils/cached-api-wrapper';
import { getCachedInvoiceImageUrl } from '../utils/invoice-image-cache';
import AdditionalFieldsModal from './additional-fields-modal';
import FormAsyncSelect from './form-async-select';
import AccessControl from "../utils/access_control";
import TaxCodeSelector from './tax-code-selector.jsx';
import { findImputationRowForExpenseEditor, resolveDisplayImputationRows, imputationDocumentId, resolveImputationRowsForExpenseEditor, resolveImputationTaxCodeFromRows, applyImputationCodingToListItem, nonBillableCostTypeOf, applyTaxCodeToExpenseJson, expenseUsesDisbursementCode, resolveExpenseType } from '../pages/review-expense/utils/contract-expense-json';
import { canValidateExpense, isAccountOpenForValidation } from '../pages/review-expense/utils/expense-review-list';
import { applyExpenseTypeToExpenseEditorJson, applyExpenseTypeToListItem, sanitizeNonClientBillableJson } from '../pages/review-expense/utils/conditional-fields';
import {
    buildActivityAdditionalFieldsFilter,
    buildBillableAdditionalFieldsFilter,
    buildGlAdditionalFieldsFilter,
    shouldShowActivityInlineTrigger,
    shouldShowHeaderGlUnitField,
    shouldShowLineGlUnitColumn,
    shouldUseInvoiceOfficeLabel,
} from '../utils/additional-fields-filter-helpers';
import {
    buildImputationsListUrl,
    compareDisplayLabelStrings,
    deletedImputationOptionLabel,
    formatImputationSelectOptionLabel,
    imputationDisplayLabel,
    resolveStoredImputationMissingFromList,
    sortImputationRowsByDisplayLabel,
} from '../utils/expenses-imputations-config-helpers';
import { formatValidateExpenseError } from '../utils/resolve-validation-message';
import { officeNameWithAliasesLabel } from '../utils/office-option-label';
import { getActivityMissingKeys } from '../utils/invoice-line-required-fields';
import { expenseAmountLockReason } from '../utils/expense-amount-correction';
import {
    captureMileageSessionBaseline,
    filterMileageErrorKeysForResolvedTravelType,
    filterMileageErrorKeysForTravelType,
    findMileageTravelType,
    getMileageFieldErrorKeys,
    getMileageFieldErrorKeysForTravelType,
    mileageSaveErrorKey,
    restoreMileageSessionBaselineFields,
    resolveMatterIdFromJson,
    shouldDeferDualTravelPersist,
    shouldDeferExpenseEditorAutoSave,
    travelTypeRequiresMatter,
} from '../utils/mileage-validation';
import {
    useAccessibleTravelTypeIds,
    filterToAccessibleTravelTypes,
} from '../utils/accessible-travel-types';

/** Matches xpens-api `DEFAULT_PAGINATION_PARAMETERS.max_page_size` for GET /offices/. */
const TYPE1_MILEAGE_OFFICES_PAGE_SIZE = 500;

/** Both route endpoints filled — required before Google Maps distance recalc on PUT. */
const hasCompleteMileageRoute = (json) => {
    const origin = String(json?.AddressOrigin ?? '').trim();
    const dest = String(json?.AddressDestination ?? '').trim();
    return origin.length > 0 && dest.length > 0;
};

/** Drop km and derived totals when the route or travel mode changes (dual-travel mileage). */
const clearMileageCalculatedAmounts = (json) => {
    delete json.Distance;
    delete json.Total;
    delete json.BigTotalPayed;
    delete json.TotalWithTax;
    delete json.Tax;
};

/**
 * Three modes as of XPEN-3246: 'office' (pick departure/destination offices),
 * 'address' (type a departure/destination route), and 'open_amount' (type the
 * amount spent — no office, no route). "Not office" no longer implies
 * "address" — check explicitly for the mode you mean, not by negation.
 */
const resolveMileageRouteMode = (json, resolvedTravelType) => {
    if (resolvedTravelType?.travel_mode) {
        if (resolvedTravelType.travel_mode === 'office_travel') return 'office';
        if (resolvedTravelType.travel_mode === 'open_amount') return 'open_amount';
        return 'address';
    }
    return (json?.travel_type ?? 'inter_office') === 'inter_office' ? 'office' : 'address';
};

const resolveMileageBillableScope = (json, resolvedTravelType) => {
    if (resolvedTravelType?.billable_scope) {
        return resolvedTravelType.billable_scope === 'billable';
    }
    if (json?.travel_type_id && [1, true].includes(json?.IsBillable)) {
        return true;
    }
    return (json?.travel_type ?? 'inter_office') === 'billable';
};

/**
 * XPEN-2866: a resolved travel type (travel_type_id model) needs a Matter based on
 * Client scope, not Billable scope. The legacy travel_type string model (no
 * travel_type_id / no resolved rates row) has no Client-scope concept at all, so it
 * falls back to the same Billable-scope check it always used.
 */
const resolveMileageMatterRequirement = (json, resolvedTravelType) => {
    if (resolvedTravelType) {
        return travelTypeRequiresMatter(resolvedTravelType);
    }
    return resolveMileageBillableScope(json, resolvedTravelType);
};

const syncMatterIdFromJson = (json) => {
    if (!json) {
        return json;
    }
    const matterId = resolveMatterIdFromJson(json);
    if (!matterId) {
        return json;
    }
    const next = { ...json, MatterId: matterId };
    if (Array.isArray(next.ListItem) && next.ListItem.length > 0) {
        const first = next.ListItem[0];
        if (!String(first?.MatterId ?? first?.MatterID ?? '').trim()) {
            next.ListItem = [{ ...first, MatterId: matterId }, ...next.ListItem.slice(1)];
        }
    }
    return next;
};

const Expense = ({ data, account_id, selected, close: onClose, account_status, account_submitted_at, isNewCreation = false, refreshExpensesAccountList, readOnly = false }) => {
    // account_submitted_at comes from the parent rather than this component's own
    // expensesAccount state, which is fetched asynchronously and would hide the
    // button until it lands. Both fields are passed so this call site matches the
    // rule the two list components apply (XPEN-3383).
    const canValidate = AccessControl.userCanValidateExpenses(data)
        && isAccountOpenForValidation({ status: account_status, submitted_at: account_submitted_at });
    // XPEN-3786: amounts of an approved or validated report need the correction permission; none once finalized.
    const amountLock = expenseAmountLockReason(data, [account_status]);
    const [expensesAccount, setExpensesAccount] = useState();
    const source = axios.CancelToken.source();
    const [pdfBuff, setPdfBuff] = useState(null);
    // Starts true, not false — the PDF effect below only flips this after the
    // first commit (a passive effect), and the desktop panel is now always
    // mounted (not gated on pdfBuff), so a false start would flash "PDF not
    // available" on every open before the effect ever runs.
    const [isLoadingPdf, setIsLoadingPdf] = useState(true);
    // Seed for PDFVisualizer's paperclip badge (XPEN-3007). Scoped to the
    // current expense document (account_id + checksum) — not the whole report.
    const [attachmentsCount, setAttachmentsCount] = useState(0);
    const attachmentsRequestGenerationRef = useRef(0);
    // PDFVisualizer is a controlled view switcher (invoice / attachments) —
    // without this, the paperclip calls a no-op default and the panel never opens.
    const [pdfView, setPdfView] = useState("invoice");
    const [expense, setExpense] = useState(() => {
        if (!selected || selected.json?.type !== 'mileage') {
            return selected;
        }
        return { ...selected, json: syncMatterIdFromJson(selected.json ?? {}) };
    });
    const [currentView, setCurrentView] = useState("data");
    const [saving, setSaving] = useState(false);
    // XPEN-3794: a submitted report is read-only for its employee, but for its denied expenses.
    const fieldsLocked = saving || readOnly;
    const [showDisapproval, setShowDisapproval] = useState(false);
    const [validating, setValidating] = useState(false);
    const [causeDenial, setCauseDenial] = useState(null);
    const [action, setAction] = useState();
    const [savingError, setSavingError] = useState(null);
    const { root: commentRoot, canLeave: canLeaveCommentDraft, onDraftChange } = useCommentDraftGuard(data, setSavingError);
    const close = (...args) => { if (canLeaveCommentDraft()) onClose(...args); };
    /** Save-triggered mileage i18n keys (XPEN-2550); translate at render — kept separate from savingError modal. */
    const [mileageValidationErrorKeys, setMileageValidationErrorKeys] = useState([]);
    const [showConfirmRestartProcess, setShowConfirmRestartProcess] = useState("false");
    const [showConfirmRestartPostProcess, setShowConfirmRestartPostProcess] = useState(false);
    const [confirmDeleteViewId, setConfirmDeleteViewId] = useState(null);
    const [billablesSelected, setBillablesSelected] = useState([]);
    const [nonBillablesSelected, setNonBillablesSelected] = useState([]);
    const [confirmDeleteItems, setConfirmDeleteItems] = useState(null);
    const [imputationRoots, setImputationRoots] = useState([]);
    const [imputationChildren, setImputationChildren] = useState([]);
    const [staleImputationRoot, setStaleImputationRoot] = useState(null);
    const [staleImputationLeaf, setStaleImputationLeaf] = useState(null);
    const imputationRootsFetchGenRef = useRef(0);
    const imputationChildrenFetchGenRef = useRef(0);
    const lastRootResolveKeyRef = useRef(null);
    const lastLeafResolveKeyRef = useRef(null);
    const imputations = useMemo(
        () => [...imputationRoots, ...imputationChildren],
        [imputationRoots, imputationChildren]
    );
    const [categoryEdited, setCategoryEdited] = useState(false);
    const [draftRestoreGeneration, setDraftRestoreGeneration] = useState(0);
    const [rates, setRates] = useState({});
    // XPEN-3481: `rates` carries every travel type the administrator configured;
    // these are the ids the report's *owner* is allowed to pick — the owner, not
    // whoever opened the editor. Only asked for a trip: a receipt never shows a
    // travel type, so there is nothing to narrow.
    const {
        allowedIds: accessibleTravelTypeIds,
        loading: accessibleTravelTypeIdsLoading,
    } = useAccessibleTravelTypeIds(
        expense.json?.type === 'mileage' ? expensesAccount?.user?.id_user : undefined
    );
    const [pobOfficeOptions, setPobOfficeOptions] = useState([]);
    /** When Type 1 lists are empty after a successful fetch: no rows vs rows but none marked POB (XPEN-2500 AC). */
    const [mileageType1EmptyHint, setMileageType1EmptyHint] = useState(null);
    /** Home office street address from profile (Type 2 billable prefill); always resolved via allOffices even when home is already POB (XPEN-2509). */
    const [profileHomeOfficeAddress, setProfileHomeOfficeAddress] = useState('');
    const profileHomeOfficeAddressRef = useRef('');
    const type1HomePrefillAppliedForChecksumRef = useRef(null);
    const type2HomePrefillAppliedForChecksumRef = useRef(null);
    const travelTypeSeedAppliedForChecksumRef = useRef(null);
    const mileageSessionBaselineRef = useRef(null);
    const pendingSaveCloseRef = useRef(false);
    /** XPEN-2821 — open-time snapshot for Cancel discard and future Restore; updated after successful Save. */
    const expenseEditorBaselineRef = useRef(null);
    /** Latest expense for PUT — synced each render so Save always persists draft edits. */
    const expenseRef = useRef(expense);
    expenseRef.current = expense;
    const accountIdRef = useRef(account_id);
    accountIdRef.current = account_id;
    const expenseJsonRef = useRef(expense?.json);
    useEffect(() => {
        expenseJsonRef.current = expense?.json;
    }, [expense?.json]);

    useEffect(() => {
        if (!expense?.checksum) {
            return;
        }
        expenseEditorBaselineRef.current = JSON.parse(JSON.stringify(expense));
    }, [expense.checksum]);
    const [showAdditionalFieldsModal, setShowAdditionalFieldsModal] = useState(false);
    const [workItem, setWorkItem] = useState(null);
    const [additionalFieldsFilter, setAdditionalFieldsFilter] = useState([]);
    const [expenseViolations, setExpenseViolations] = useState([]);
    const viewSettings = AccessControl.getViewValue(data, "expense_editor");

    const simpleView = viewSettings?.variant === "simplified";
    const unadjusted_total_view = viewSettings?.settings?.totals?.calculation_mode === "raw"
    const showLineGlUnitColumn = shouldShowLineGlUnitColumn(data);

    const [taxeCodes, setTaxeCodes] = useState([]);
    const [taxesPercentage, setTaxesPercentage] = useState({});
    const [defaultTaxCode, setDefaultTaxCode] = useState(resolveDefaultTaxCode(data.config?.tax_profile));
    const [defaultTaxedCode, setDefaultTaxedCode] = useState(resolveTaxedCode(data.config?.tax_profile));

    useEffect(() => { setCategoryEdited(false); }, [selected]);

    useEffect(() => {
        setStaleImputationRoot(null);
        setStaleImputationLeaf(null);
        setCategoryEdited(false);
    }, [expense.checksum]);

    useEffect(() => {
        setTaxeCodes(data.config?.tax_profile?.map(t => t.code) ?? []);
        setTaxesPercentage(data.config?.tax_profile?.reduce((acc, t) => {
            acc[t.code] = t.adjusted_rates ?? t.list_rates;
            return acc;
        }, {}));
        setDefaultTaxCode(resolveDefaultTaxCode(data.config?.tax_profile));
        setDefaultTaxedCode(resolveTaxedCode(data.config?.tax_profile));
    }, [data.config]);



    const styleSelect = {
        container: (base) => ({
            ...base,
            width: '100%',
            minWidth: 0,
        }),
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: "0 !important",
            boxShadow: state.isFocused ? "0px 0px 10px #F39800 " : "0 !important",
            padding: data.isMobile ? "0" : baseStyles.padding,
            marginLeft: -14,
        })
    };


    const getMatterInfo = (matterId, callback) => {
        if (!matterId) return callback(null);
        cachedFetch(`/matters/search/?text=${matterId}`, "GET", {}, (response) => {
            if (callback && response?.data && response.data?.length > 0) {
                return callback(response.data[0]);
            }
            callback(null);
        });
    };

    const activityMap = (activity) => {
        if (typeof activity !== "object" || activity === null) {
            return null;
        }

        const activity_description = activity.description;
        const activity_code = activity.code;
        const activity_id = activity.activity_id;

        if ((activity_description || activity_code) && activity_id) {
            const option = { description: activity_description, code: activity_code, activity_id: activity_id, pta_group: activity.pta_group, pta_group_cost: activity.pta_group_cost };
            return {
                value: option,
                label: (
                    <span>
                        <strong>{activity_code}</strong> - {activity_description}
                    </span>
                )
            };
        }
        return null;
    };

    const updateItemForModal = (val, path1, path2, viewId, fieldName) => {
        const listItem = expense.json.ListItem ?? [];
        const item = listItem.find(it => it._view_id === viewId);
        if (!item) return;
        if (fieldName !== undefined) {
            const updatedItem = { ...item, [fieldName]: val };
            updateItem(updatedItem);
            setWorkItem(updatedItem);
            return;
        }
        // Full-object updates come from AdditionalFieldsModal (e.g. updateWorkItemWithTotal)
        // where val is already the merged line snapshot — no need to spread over item again.
        if (val && typeof val === 'object') {
            updateItem(val);
            setWorkItem(val);
        }
    };

    const openAdditionalFieldsForLine = (item, tableKind) => {
        setWorkItem(item);
        setAdditionalFieldsFilter(
            tableKind === 'billable'
                ? buildBillableAdditionalFieldsFilter(data, { model: expense.json?.Model })
                : buildGlAdditionalFieldsFilter(data, { isLegalEngine: Utils.isEngineType(data, 'legal') })
        );
        setShowAdditionalFieldsModal(true);
    };

    const loadOffices = (search_query = "", callback = () => {
    }) => {
        const val = search_query.trim().replace(/\//g, '');
        const filters = [{ id: "search", value: val }, { id: "id_client", value: data.id_client }];
        const sorting = [{ id: "name", desc: false }];
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}`;
        fetch(`/offices/${searchData}`, "GET", {}, (response) => {
            callback(response.data.offices.map(o => ({
                label: o.name,
                value: o.name
            })))
        })
    }

    useEffect(() => {
        if (expense.json?.type !== 'mileage') {
            setPobOfficeOptions([]);
            setMileageType1EmptyHint(null);
            setProfileHomeOfficeAddress('');
            profileHomeOfficeAddressRef.current = '';
            return;
        }
        let cancelled = false;
        const sorting = [{ id: 'name', desc: false }];
        const pobFilters = [
            { id: 'id_client', value: data.id_client },
            { id: 'is_place_of_business', value: true },
        ];
        const buildOfficesQuery = (filters, start, pageSize) =>
            `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify(sorting))}&start=${start}&page_size=${pageSize}`;

        const onFetchError = () => {
            if (cancelled) return;
            setPobOfficeOptions([]);
            setMileageType1EmptyHint(null);
            setProfileHomeOfficeAddress('');
            profileHomeOfficeAddressRef.current = '';
        };

        const applyEmptyHintProbe = () => {
            const probeFilters = [{ id: 'id_client', value: data.id_client }];
            fetch(
                `/offices/${buildOfficesQuery(probeFilters, 0, 1)}`,
                'GET',
                {},
                (probeRes) => {
                    if (cancelled) return;
                    const anyTotal = Number(probeRes?.data?.meta?.totalRowCount);
                    const safeAny = Number.isFinite(anyTotal) ? anyTotal : 0;
                    setMileageType1EmptyHint(safeAny === 0 ? 'no_offices' : 'no_pob');
                    setPobOfficeOptions([]);
                },
                onFetchError
            );
        };

        const finalizeType1Offices = (mergedOptions, lookupRows) => {
            if (cancelled) return;
            const profileHomeRaw = expensesAccount?.user?.home_office_id ?? data.home_office_id;
            const profileHomeId = imputationDocumentId({ _id: profileHomeRaw });
            let profileHomeAddr = '';
            if (profileHomeId) {
                const homeRow = lookupRows.find(
                    (o) => imputationDocumentId({ _id: o._id }) === profileHomeId
                );
                profileHomeAddr =
                    homeRow?.address != null && homeRow.address !== ''
                        ? String(homeRow.address)
                        : '';
            }
            profileHomeOfficeAddressRef.current = profileHomeAddr;
            setProfileHomeOfficeAddress(profileHomeAddr);
            if (mergedOptions.length > 0) {
                setMileageType1EmptyHint(null);
                setPobOfficeOptions(mergedOptions);
                return;
            }
            applyEmptyHintProbe();
        };

        const fetchPobPage = (start, accumulated) => {
            fetch(
                `/offices/${buildOfficesQuery(pobFilters, start, TYPE1_MILEAGE_OFFICES_PAGE_SIZE)}`,
                'GET',
                {},
                (response) => {
                    if (cancelled) return;
                    const rows = response?.data?.offices ?? [];
                    const totalRowCount = Number(response?.data?.meta?.totalRowCount);
                    // Without valid meta we cannot know if more pages exist; use a one-page ceiling so we
                    // stop rather than risk a runaway loop. xpens-api returns numeric totalRowCount today.
                    const safeTotal = Number.isFinite(totalRowCount)
                        ? totalRowCount
                        : accumulated.length + rows.length;
                    const nextAll = [...accumulated, ...rows];
                    if (rows.length > 0 && nextAll.length < safeTotal) {
                        fetchPobPage(start + rows.length, nextAll);
                        return;
                    }
                    const allPobOffices = nextAll;
                    const pobOnly = allPobOffices.filter((o) => o.active !== false && Boolean(o.is_place_of_business));
                    const baseOptions = pobOnly.map((o) => ({
                        value: imputationDocumentId({ _id: o._id }),
                        label: officeNameWithAliasesLabel(o),
                        raw: o,
                    }));
                    const profileHomeRaw = expensesAccount?.user?.home_office_id ?? data.home_office_id;
                    const profileHomeId = imputationDocumentId({ _id: profileHomeRaw });
                    let mergedOptions = baseOptions;
                    let lookupRows = [...allPobOffices];

                    if (profileHomeId && !baseOptions.some((o) => o.value === profileHomeId)) {
                        const homeRowForMerge = allPobOffices.find(
                            (o) => imputationDocumentId({ _id: o._id }) === profileHomeId
                        );
                        if (homeRowForMerge) {
                            mergedOptions = [
                                {
                                    value: imputationDocumentId({ _id: homeRowForMerge._id }),
                                    label: officeNameWithAliasesLabel(homeRowForMerge),
                                    raw: homeRowForMerge,
                                },
                                ...baseOptions,
                            ];
                            finalizeType1Offices(mergedOptions, lookupRows);
                            return;
                        }
                        const homeFilters = [
                            { id: 'id_client', value: data.id_client },
                            { id: '_id', value: profileHomeId },
                        ];
                        fetch(
                            `/offices/${buildOfficesQuery(homeFilters, 0, 1)}`,
                            'GET',
                            {},
                            (homeRes) => {
                                if (cancelled) return;
                                const row = (homeRes?.data?.offices ?? [])[0];
                                if (row && imputationDocumentId({ _id: row._id }) === profileHomeId) {
                                    mergedOptions = [
                                        {
                                            value: imputationDocumentId({ _id: row._id }),
                                            label: officeNameWithAliasesLabel(row),
                                            raw: row,
                                        },
                                        ...baseOptions,
                                    ];
                                    lookupRows = [...allPobOffices, row];
                                }
                                finalizeType1Offices(mergedOptions, lookupRows);
                            },
                            () => finalizeType1Offices(mergedOptions, lookupRows)
                        );
                        return;
                    }
                    finalizeType1Offices(mergedOptions, lookupRows);
                },
                onFetchError
            );
        };

        fetchPobPage(0, []);

        return () => {
            cancelled = true;
        };
    }, [
        data.id_client,
        data.home_office_id,
        expensesAccount?.user?.home_office_id,
        expense.json?.type,
    ]);

    useEffect(() => {
        type1HomePrefillAppliedForChecksumRef.current = null;
        type2HomePrefillAppliedForChecksumRef.current = null;
        travelTypeSeedAppliedForChecksumRef.current = null;
        mileageSessionBaselineRef.current = null;
    }, [expense.checksum]);

    useEffect(() => {
        if (expense.json?.type !== 'mileage') return;
        if (mileageSessionBaselineRef.current?.checksum === expense.checksum) return;
        const travelTypeId = expense.json?.travel_type_id;
        const resolvedTravelType = findMileageTravelType(rates, travelTypeId);
        if (travelTypeId && !resolvedTravelType) return;
        mileageSessionBaselineRef.current = captureMileageSessionBaseline(
            syncMatterIdFromJson(expense.json ?? {}),
            expense.checksum,
            resolvedTravelType
        );
    }, [
        expense.checksum,
        expense.json?.travel_type_id,
        expense.json?.type,
        rates?.mileage_travel_types,
    ]);

    const computeTaxes = (targetExpense, onSuccess = null) => {
        const expenseJson = targetExpense?.json;
        if (!expenseJson) return;
        
        fetch('/utils/compute_taxes', 'POST', expenseJson, (response) => {
            const json = response.data.json;
            const updatedExpense = { ...targetExpense, json };
            setExpense(updatedExpense);
            schedulePersist();
            onSuccess?.(updatedExpense);
        });
    };

    const updateItem = (item, updateTaxes = false) => {
        let listItem = expense.json.ListItem ?? [];
        const index = listItem.findIndex(it => it._view_id === item._view_id);
        if (index === -1) {
            listItem.push({ ...item });
        }
        else {
            listItem[index] = { ...item };
        }

        const updatedExpense = {
            ...expense,
            json: { ...expense.json, ListItem: [...listItem] }
        };

        if (updateTaxes) {
            computeTaxes(updatedExpense);
            return;
        }

        setExpense(updatedExpense);
        schedulePersist();
    }

    useEffect(() => () => source.cancel("Expense unmounted"), []);
    useEffect(() => {
        fetchExpensesAccount()
        fetchRates()
    }, []);

    useEffect(() => {
        if (!expensesAccount) return;
        const ownerId = expensesAccount?.user?.id_user;
        const fetchGen = ++imputationRootsFetchGenRef.current;
        const rootFilters = [{ id: 'roots_only', value: true }];
        const expenseType = expense.json?.ExpenseType;
        if (expenseType === 'client' || expenseType === 'internal') {
            rootFilters.push({ id: 'client_filter', value: expenseType });
        }
        fetch(
            buildImputationsListUrl(rootFilters, ownerId, data.id_client),
            'GET',
            {},
            (response) => {
                if (fetchGen !== imputationRootsFetchGenRef.current) return;
                const roots = response?.data?.imputations ?? [];
                const hasClientRoots = roots.some((r) => r.origin === 'client');
                const filteredRoots = hasClientRoots ? roots.filter((r) => r.origin === 'client') : roots;
                setImputationRoots(filteredRoots);
                const l1 = expenseRef.current?.json?.ImputationLevel1Id;
                const lookedUpId = l1 != null ? String(l1) : '';
                const checksumAtResolve = expenseRef.current?.checksum;
                lastRootResolveKeyRef.current = `${checksumAtResolve}:${lookedUpId}`;
                resolveStoredImputationMissingFromList(
                    {
                        storedId: l1,
                        liveRows: filteredRoots,
                        reviewForUserId: ownerId,
                        fetchImpl: fetch,
                    },
                    (result) => {
                        if (fetchGen !== imputationRootsFetchGenRef.current) return;
                        if (expenseRef.current?.checksum !== checksumAtResolve) return;
                        if (String(expenseRef.current?.json?.ImputationLevel1Id ?? '') !== lookedUpId) return;
                        if (result.action === 'deleted') {
                            setStaleImputationRoot(result.row);
                            return;
                        }
                        if (result.action === 'error') {
                            return;
                        }
                        setStaleImputationRoot(null);
                        if (result.action === 'clear' && lookedUpId) {
                            setExpense((prev) => {
                                if (String(prev.json?.ImputationLevel1Id ?? '') !== lookedUpId) return prev;
                                return {
                                    ...prev,
                                    json: {
                                        ...prev.json,
                                        ImputationLevel1Id: null,
                                        ImputationLeafId: null,
                                    },
                                };
                            });
                        }
                    }
                );
            },
            () => {
                if (fetchGen !== imputationRootsFetchGenRef.current) return;
                setImputationRoots([]);
            }
        );
    }, [expensesAccount, expense.json?.ExpenseType, data.id_client]);

    useEffect(() => {
        if (!expensesAccount) return;
        const level1Id = expense.json?.ImputationLevel1Id;
        if (!level1Id) {
            imputationChildrenFetchGenRef.current += 1;
            lastLeafResolveKeyRef.current = null;
            setImputationChildren([]);
            setStaleImputationLeaf(null);
            return;
        }
        const ownerId = expensesAccount?.user?.id_user;
        const fetchParentId = String(level1Id);
        const fetchGen = ++imputationChildrenFetchGenRef.current;
        lastLeafResolveKeyRef.current = null;
        const childFilters = [{ id: 'parent_id', value: fetchParentId }];
        const expenseType = expense.json?.ExpenseType;
        if (expenseType === 'client' || expenseType === 'internal') {
            childFilters.push({ id: 'client_filter', value: expenseType });
        }
        setImputationChildren([]);
        fetch(
            buildImputationsListUrl(childFilters, ownerId, data.id_client),
            'GET',
            {},
            (response) => {
                if (fetchGen !== imputationChildrenFetchGenRef.current) return;
                if (String(expenseRef.current?.json?.ImputationLevel1Id) !== fetchParentId) {
                    return;
                }
                const rows = response?.data?.imputations ?? [];
                const hasClientChildren = rows.some((r) => r.origin === 'client');
                const filteredRows = hasClientChildren ? rows.filter((r) => r.origin === 'client') : rows;
                setImputationChildren(filteredRows);
                const leafId = expenseRef.current?.json?.ImputationLeafId;
                const lookedUpId = leafId != null ? String(leafId) : '';
                const checksumAtResolve = expenseRef.current?.checksum;
                lastLeafResolveKeyRef.current = `${checksumAtResolve}:${lookedUpId}`;
                resolveStoredImputationMissingFromList(
                    {
                        storedId: leafId,
                        liveRows: filteredRows,
                        reviewForUserId: ownerId,
                        fetchImpl: fetch,
                    },
                    (result) => {
                        if (fetchGen !== imputationChildrenFetchGenRef.current) return;
                        if (expenseRef.current?.checksum !== checksumAtResolve) return;
                        if (String(expenseRef.current?.json?.ImputationLeafId ?? '') !== lookedUpId) return;
                        if (result.action === 'deleted') {
                            setStaleImputationLeaf(result.row);
                            return;
                        }
                        if (result.action === 'error') {
                            return;
                        }
                        setStaleImputationLeaf(null);
                        if (result.action === 'clear' && lookedUpId) {
                            setExpense((prev) => {
                                if (String(prev.json?.ImputationLeafId ?? '') !== lookedUpId) return prev;
                                return {
                                    ...prev,
                                    json: {
                                        ...prev.json,
                                        ImputationLeafId: null,
                                    },
                                };
                            });
                        }
                    }
                );
            },
            () => {
                if (fetchGen !== imputationChildrenFetchGenRef.current) return;
                if (String(expenseRef.current?.json?.ImputationLevel1Id) !== fetchParentId) {
                    return;
                }
                setImputationChildren([]);
            }
        );
    }, [expensesAccount, expense.json?.ExpenseType, expense.json?.ImputationLevel1Id, data.id_client]);

    useEffect(() => {
        if (!expensesAccount) return;
        if (lastRootResolveKeyRef.current == null) return;
        const l1 = expense.json?.ImputationLevel1Id;
        const lookedUpId = l1 != null ? String(l1) : '';
        const key = `${expense.checksum}:${lookedUpId}`;
        if (lastRootResolveKeyRef.current === key) return;
        lastRootResolveKeyRef.current = key;
        resolveStoredImputationMissingFromList(
            {
                storedId: l1,
                liveRows: imputationRoots,
                reviewForUserId: expensesAccount?.user?.id_user,
                fetchImpl: fetch,
            },
            (result) => {
                if (lastRootResolveKeyRef.current !== key) return;
                if (String(expenseRef.current?.json?.ImputationLevel1Id ?? '') !== lookedUpId) return;
                if (result.action === 'deleted') {
                    setStaleImputationRoot(result.row);
                    return;
                }
                if (result.action === 'error') {
                    return;
                }
                setStaleImputationRoot(null);
                if (result.action === 'clear' && lookedUpId) {
                    setExpense((prev) => {
                        if (String(prev.json?.ImputationLevel1Id ?? '') !== lookedUpId) return prev;
                        return {
                            ...prev,
                            json: {
                                ...prev.json,
                                ImputationLevel1Id: null,
                                ImputationLeafId: null,
                            },
                        };
                    });
                }
            }
        );
    }, [expensesAccount, imputationRoots, expense.checksum, expense.json?.ImputationLevel1Id]);

    useEffect(() => {
        if (!expensesAccount) return;
        if (lastLeafResolveKeyRef.current == null) return;
        const leafId = expense.json?.ImputationLeafId;
        const lookedUpId = leafId != null ? String(leafId) : '';
        const key = `${expense.checksum}:${lookedUpId}`;
        if (lastLeafResolveKeyRef.current === key) return;
        lastLeafResolveKeyRef.current = key;
        resolveStoredImputationMissingFromList(
            {
                storedId: leafId,
                liveRows: imputationChildren,
                reviewForUserId: expensesAccount?.user?.id_user,
                fetchImpl: fetch,
            },
            (result) => {
                if (lastLeafResolveKeyRef.current !== key) return;
                if (String(expenseRef.current?.json?.ImputationLeafId ?? '') !== lookedUpId) return;
                if (result.action === 'deleted') {
                    setStaleImputationLeaf(result.row);
                    return;
                }
                if (result.action === 'error') {
                    return;
                }
                setStaleImputationLeaf(null);
                if (result.action === 'clear' && lookedUpId) {
                    setExpense((prev) => {
                        if (String(prev.json?.ImputationLeafId ?? '') !== lookedUpId) return prev;
                        return {
                            ...prev,
                            json: {
                                ...prev.json,
                                ImputationLeafId: null,
                            },
                        };
                    });
                }
            }
        );
    }, [expensesAccount, imputationChildren, expense.checksum, expense.json?.ImputationLeafId]);

    useEffect(() => {
        fetchExpenseRuleViolation()
    }, [expense])

    const fetchExpenseRuleViolation = () => {
        let formData = new FormData();
        formData.append("expense", JSON.stringify(expense));

        fetch(`/expenses_rules_evaluation/`, "POST", formData,
            (response) => {
                setExpenseViolations(response.data?.violations);
            }
            ,
            (error) => {

            })
    }

    const fetchExpensesAccount = () => {
        const searchData = `?account_id=${encodeURIComponent(account_id)}`;

        fetch(`/expenses_account/${searchData}`, "GET", {},
            (response) => {
                setExpensesAccount(response.data.expenses_account);
            }
            ,
            (error) => {
                if (error.code === "ERR_NETWORK")
                    fetchExpensesAccount();
            }
            , false, source.token);
    }

    const abortSaveAndShowError = (message) => {
        setSaving(false);
        pendingSaveCloseRef.current = false;
        setSavingError(message);
    };

    const deferDualTravelPersist = () =>
        shouldDeferDualTravelPersist(data, expense.json?.type);

    const deferExpenseEditorAutoSave = () =>
        shouldDeferExpenseEditorAutoSave(expense.json?.type);

    const resolvedMileageTravelType = useMemo(
        () => findMileageTravelType(rates, expense.json?.travel_type_id),
        [rates?.mileage_travel_types, expense.json?.travel_type_id]
    );

    const isMileageOfficeTravel = () =>
        resolveMileageRouteMode(expense.json, resolvedMileageTravelType) === 'office';

    const isMileageOpenAmount = () =>
        resolveMileageRouteMode(expense.json, resolvedMileageTravelType) === 'open_amount';

    const isMileageBillableScope = () =>
        resolveMileageBillableScope(expense.json, resolvedMileageTravelType);

    const schedulePersist = ({ immediate = false } = {}) => {
        if (immediate || !deferExpenseEditorAutoSave()) {
            setSaving(true);
        }
    };

    useEffect(() => {
        if (saving !== true) {
            return;
        }
        if (readOnly) {
            setSaving(false);
            return;
        }
        updateExpense();
    }, [saving])

    useEffect(() => {
        if (expense.json?.type === 'mileage') {
            // A mileage expense never shows the PDF viewer, but a pending
            // request from the previous (non-mileage) expense could still
            // resolve and get rejected by the staleness guard below without
            // ever clearing isLoadingPdf/pdfBuff — leaving a stuck spinner or
            // a stale receipt's buffer around for as long as this one is open.
            setIsLoadingPdf(false);
            setPdfBuff(null);
            return;
        }

        // Pinned so a resolution can tell whether it's still current — two
        // requests can resolve out of order, and a stale one landing after a
        // newer request (same or different account) started must not
        // overwrite it with the wrong receipt.
        const requestedChecksum = expense.checksum;
        const requestedAccountId = account_id;
        setIsLoadingPdf(true);
        // Clear the previous receipt's buffer immediately — otherwise the viewer
        // (and a Download PDF click) can show/save the previous receipt's bytes
        // under the new receipt's id while this fetch is still in flight.
        setPdfBuff(null);
        getCachedInvoiceImageUrl(`${account_id}_${requestedChecksum}.pdf`, source.token)
            .then(url => {
                if (requestedChecksum !== expenseRef.current?.checksum || requestedAccountId !== accountIdRef.current) return;
                if (url) {
                  setPdfBuff(url);
                }
                setIsLoadingPdf(false);
            })
            .catch(() => {
                if (requestedChecksum !== expenseRef.current?.checksum || requestedAccountId !== accountIdRef.current) return;
                setIsLoadingPdf(false);
                setPdfBuff(null);
            });
    }, [expense.checksum, account_id])

    // XPEN-3007 — seed the paperclip badge for the current expense document.
    // Mirrors invoice.js loadAttachmentsCount: bump a generation so a stale
    // response from a previous receipt cannot overwrite the current count.
    useEffect(() => {
        attachmentsRequestGenerationRef.current += 1;
        const requestedGeneration = attachmentsRequestGenerationRef.current;

        // Clear immediately so paging between receipts cannot flash the
        // previous document's count under the new ownerSubId (invoice.js
        // resets at navigation for the same reason).
        setAttachmentsCount(0);

        // PDFVisualizer (and its paperclip) never renders on mobile or for
        // mileage — nothing to seed a badge for.
        if (data.isMobile || expense.json?.type === 'mileage' || !expense.checksum || !account_id) {
            return;
        }

        fetch(
            buildAttachmentsListUrl('expense', account_id, expense.checksum),
            'GET',
            {},
            (response) => {
                if (requestedGeneration !== attachmentsRequestGenerationRef.current) return;
                setAttachmentsCount(resolveAttachmentsCount(response));
            },
            () => {
                if (requestedGeneration !== attachmentsRequestGenerationRef.current) return;
                setAttachmentsCount(0);
            }
        );
    }, [expense.checksum, account_id, data.isMobile, expense.json?.type]);

    // Leave the attachments tab when the user pages to another receipt — the
    // previous document's panel must not stay selected under the new id.
    useEffect(() => {
        setPdfView("invoice");
    }, [expense.checksum, account_id]);

    const fetchRates = () => {
        fetch("/expense_rates/", "GET", {}, (response) => {
            const fetchedRates = response.data?.rates;
            setRates(fetchedRates);
        });
    };

    const showStatus = (value) => {
        return (value != null && value !== "") || data.config.frontend_invoice == null || !data.config.frontend_invoice.hide_na_fields ? "flex" : "none";
    }

    useEffect(() => {
        setExpense((prev) => {
            const sanitizedJson = sanitizeNonClientBillableJson(prev.json ?? {});
            if (JSON.stringify(sanitizedJson) === JSON.stringify(prev.json ?? {})) {
                return prev;
            }
            return { ...prev, json: sanitizedJson };
        });
    }, [expense.checksum]);

    const updateExpense = (action = 'update') => {
        setAction(action);
        const currentExpense = expenseRef.current ?? expense;
        // XPEN-2658: normalize legacy null-typed receipts before PUT — API rejects null type.
        const payload = {
            ...currentExpense,
            json: sanitizeNonClientBillableJson({
                ...currentExpense.json,
                type: currentExpense.json?.type ?? 'receipt',
            }),
        };
        fetch("/expenses/" + account_id + "/" + currentExpense.checksum, "PUT", payload,
            response => {
                setSaving(false);
                setSavingError(null);
                setMileageValidationErrorKeys([]);
                setExpense(response.data.expense);
                expenseEditorBaselineRef.current = JSON.parse(JSON.stringify(response.data.expense));
                fetchExpensesAccount();
                if (pendingSaveCloseRef.current) {
                    pendingSaveCloseRef.current = false;
                    // Close-on-save only fires via the manual Save button, which now renders
                    // for mileage only (XPEN-2872) — so this is always a mileage save.
                    close({ savedExpense: response.data.expense, mileageSaved: true });
                }
            },
            error => {
                // XPEN-3481: a travel type reserved for another department is refused
                // by the server. Say which it was, instead of the generic sentence —
                // "something went wrong" gives the traveller nothing to act on. Every
                // other failure keeps the editor's existing wording.
                abortSaveAndShowError(
                    data.t.read(mileageSaveErrorKey(error, invoiceSaveErrorKey(error)))
                );
            })
    }

    const handleCancelCreation = () => {
        if (!canLeaveCommentDraft()) return;
        if (saving || validating) return;
        fetch(
            `/expenses_account/expenses/${account_id}`,
            'DELETE',
            { checksums: [expense.checksum] },
            () => close(),
            () => setSavingError(data.t.read('internal_saving_error')),
            false,
            source.token
        );
    };

    const handleCloseExpense = () => {
        if (isNewCreation) {
            handleCancelCreation();
            return;
        }
        close();
    };

    const handleManualSave = () => {
        if (expense.json?.type === 'mileage') {
            const json = expenseJsonRef.current ?? expense.json;
            let errorKeys;
            if (json?.travel_type_id && resolvedMileageTravelType) {
                errorKeys = getMileageFieldErrorKeysForTravelType(json, resolvedMileageTravelType);
            } else {
                const travelType = json?.travel_type ?? 'inter_office';
                errorKeys = filterMileageErrorKeysForTravelType(
                    getMileageFieldErrorKeys(json, data),
                    travelType
                );
            }
            if (errorKeys.length > 0) {
                setMileageValidationErrorKeys(errorKeys);
                pendingSaveCloseRef.current = false;
                return;
            }
            setMileageValidationErrorKeys([]);
        }
        pendingSaveCloseRef.current = true;
        if (saving || validating) {
            return;
        }
        setSaving(true);
    };

    const showManualSaveFooter = deferExpenseEditorAutoSave();

    const expenseEditorBaseline = expenseEditorBaselineRef.current;
    const isExpenseEditorDraftDirty = showManualSaveFooter
        && expenseEditorBaseline
        && JSON.stringify(expense.json) !== JSON.stringify(expenseEditorBaseline.json);

    const handleRestoreOriginal = () => {
        const baseline = expenseEditorBaselineRef.current;
        if (!baseline || saving || validating) {
            return;
        }
        setExpense(JSON.parse(JSON.stringify(baseline)));
        setCategoryEdited(false);
        setMileageValidationErrorKeys([]);
        setDraftRestoreGeneration((generation) => generation + 1);
    };

    const validateExpense = () => {
        if (!canLeaveCommentDraft()) return;
        // XPEN-3375: single choke point. The two UI entry points are gated too, but
        // guarding here means a future caller — or a re-enabled status Select that
        // forgets its own guard — cannot ship an ungated PUT. Visibility only; the
        // server-side check is XPEN-3378.
        if (!canValidate) return;
        setValidating(true);
        const currentExpense = expenseRef.current ?? expense;
        // XPEN-2658: normalize legacy null-typed receipts before PUT — API rejects null type.
        const payload = {
            ...currentExpense,
            json: { ...currentExpense.json, type: currentExpense.json?.type ?? 'receipt' },
        };
        fetch(`/expenses/validate/${account_id}/${currentExpense.checksum}`, "PUT", payload,
            response => {
                setValidating(false);
                setExpense(response.data.expense);
                getNextExpense(true, true);
                fetchExpensesAccount();
                // XPEN-3233: the page behind this popup keeps its own copy of the
                // account for the list's checkmark/status column — refresh it here too,
                // not only when the popup closes, so it never shows a stale status.
                // Optional chaining: the status Select's onChange (XPEN-2658) shares this
                // success path — currently unreachable via the UI since that Select is
                // hardcoded disabled, but reachable in tests / if it's ever re-enabled,
                // and it never passes this prop — must not crash there.
                refreshExpensesAccountList?.();
            },
            error => {
                setValidating(false);
                setSavingError(formatValidateExpenseError(data.t, error?.response?.data?.message));
            })
    }

    const restartProcess = () => {
        fetch(`/expenses/restart-processing/${account_id}/${expense.checksum}`, "GET", {},
            response => {
                close();
            },
            null, false, source.token);
    }

    const restartPostProcess = () => {
        fetch(`/expenses/restart-rules/${account_id}/${expense.checksum}`, "GET", {},
            response => {
                setExpense(response.data.expense);
                fetchExpensesAccount();
                setShowConfirmRestartPostProcess(false)
            },
            null, false, source.token);
    }

    const isItemBillable = (item) => {
        return item.IsBillable;
    }

    /** XPEN-2806 / XPEN-2798 — disbursement vs GL table routing (billable OR client). */
    const itemUsesDisbursementCode = (item) =>
        expenseUsesDisbursementCode(expense.json ?? {}, item);

    const recodeLineImputation = (item, json = expense.json) => {
        const { level1Row, leafRow } = resolveImputationRowsForExpenseEditor(imputations, json);
        return applyImputationCodingToListItem(item, json, {
            level1Row,
            leafRow,
            preserveExistingWhenEmpty: true,
            nonBillableCostType: nonBillableCostTypeOf(data),
        });
    };

    const hasDisbursementItems = () => {
        return expense.json?.ListItem?.some(item => itemUsesDisbursementCode(item)) ?? false;
    };

    const hasGlItems = () => {
        return expense.json?.ListItem?.some(item => !itemUsesDisbursementCode(item)) ?? false;
    };

    const updateExpenseType = (choice) => {
        const nextType = choice?.value ?? '';
        let nextJson = applyExpenseTypeToExpenseEditorJson(expense.json ?? {}, nextType);
        const listItems = (nextJson.ListItem || []).map((item) =>
            recodeLineImputation(applyExpenseTypeToListItem(item, nextType), nextJson)
        );
        nextJson = { ...nextJson, ListItem: listItems };
        setExpense(prev => ({ ...prev, json: nextJson }));
        // XPEN-2872: persist the ExpenseType change (no-op for mileage, which still defers to manual Save).
        schedulePersist();
    };

    const isInternalExpenseType = resolveExpenseType(expense.json ?? {}) === 'internal';

    const addItem = (billable = false) => {
        if (isInternalExpenseType && billable) {
            return;
        }
        const listItem = expense.json.ListItem ?? [];
        const itemTaxCode = expense.json.TaxCode ?? defaultTaxedCode;
        const t2 = taxesPercentage[itemTaxCode]?.[1] ?? 0;
        let itemTotal = listItem?.length === 0 ? expense.json.Total : 0;

        if (billable && "BCGSTPSTIN" === expense.json.TaxCode) {
            itemTotal = itemTotal / (1 + t2);
        }

        const imputation = findImputationRowForExpenseEditor(imputations, expense?.json);
        const draftIsBillable = billable ? 1 : 0;
        const usesDisbursement = expenseUsesDisbursementCode(expense.json ?? {}, { IsBillable: draftIsBillable });

        const item = {
            _view_id: listItem?.length,
            IsBillable: draftIsBillable,
            TaxCode: itemTaxCode,
            Description: expense?.json?.type === 'mileage' ? categoryLabel(expense?.json?.Category) : '',
            Total: itemTotal,
            GLOffice: expense.json?.GLOffice ?? expense.json?.Office,
            Office: expense.json?.Office,
            ...(usesDisbursement ? { CostType: imputation?.cost_type, GLNatural: '' } : { GLNatural: imputation?.gl_natural, CostType: '' }),
            ...(usesDisbursement ? { MatterId: expense.json?.MatterId, WorkDate: expense.json?.InvoiceDate } : {}),
        }
        updateItem(item, true);
    }

    const toggleItemSelected = (section, item) => {
        let list = section === "billable" ? billablesSelected : nonBillablesSelected;
        let idx = list.findIndex(it => it._view_id === item._view_id);

        if (idx !== -1) {
            list.splice(idx, 1)
        }
        else {
            list.push(item);
        }

        section === "billable" ? setBillablesSelected([...list]) : setNonBillablesSelected([...list]);

    }

    const deleteItem = (viewId) => {
        const listItem = (expense.json?.ListItem || []).filter(item => item._view_id !== viewId)
            .map((item, index) => ({
                ...item, // Spread the existing properties
                "_view_id": index,
            }
            ));
        setExpense(prev => ({ ...prev, json: { ...prev.json, ListItem: [...listItem] } }));
        schedulePersist();
        setConfirmDeleteViewId(null);
        setNonBillablesSelected([])
        setBillablesSelected([])
    }

    const confirmDeleteAction = (section) => {
        let list = section === "billable" ? billablesSelected : nonBillablesSelected;
        setConfirmDeleteItems(list);
        section === "billable" ? setBillablesSelected([]) : setNonBillablesSelected([]);
    }

    const deleteItems = () => {
        const listItem = (expense.json?.ListItem || []).filter(item => !confirmDeleteItems.includes(item))
            .map((item, index) => ({
                ...item, // Spread the existing properties
                "_view_id": index,
            }
            ));
        setExpense(prev => ({ ...prev, json: { ...prev.json, ListItem: [...listItem] } }));
        schedulePersist();
        setNonBillablesSelected([])
        setBillablesSelected([])
        setConfirmDeleteItems(null);
    }



    const toggleSelectAll = (sectionName) => {
        if (sectionName === "non-billable")
            setNonBillablesSelected(expense.json.ListItem && expense.json.ListItem.filter(item => !itemUsesDisbursementCode(item))?.length !== nonBillablesSelected?.length ? [...expense.json.ListItem.filter(item => !itemUsesDisbursementCode(item))] : [])
        else
            setBillablesSelected(expense.json.ListItem && expense.json.ListItem.filter(item => itemUsesDisbursementCode(item))?.length !== billablesSelected?.length ? [...expense.json.ListItem.filter(item => itemUsesDisbursementCode(item))] : [])
    }


    /**
     * when getting the next expense we call ask to remove it from list 
     * @param {boolean} remove 
     * @param {boolean} forward 
     */
    const getNextExpense = (remove = true, forward = true) => {
        if (!canLeaveCommentDraft()) return;
        let nextIndex;
        setBillablesSelected([])
        setNonBillablesSelected([])
        const indexCurrentInvoice = expensesAccount?.expenses.findIndex(
            xpen => xpen.checksum === expense.checksum
        );

        if (forward === true) {
            if (indexCurrentInvoice + 1 < expensesAccount?.expenses?.length) {
                nextIndex = indexCurrentInvoice + 1
            }
            else {
                nextIndex = 0
            }
        }

        else if (forward === false) {
            if (indexCurrentInvoice - 1 >= 0)
                nextIndex = indexCurrentInvoice - 1;
            else
                nextIndex = expensesAccount?.expenses?.length - 1;
        }

        if (remove === true) {
            expensesAccount?.expenses.splice(indexCurrentInvoice, 1);
            nextIndex = indexCurrentInvoice;
        }

        if (expensesAccount?.expenses?.length > nextIndex) {
            setExpense(expensesAccount?.expenses[nextIndex]);
        }
        else
            close();
    }

    const getNextTaxCode = (taxCode) => {
        let indexNextCode = taxeCodes.findIndex(el => el === taxCode) + 1;
        indexNextCode = indexNextCode >= taxeCodes?.length ? 0 : indexNextCode;
        return taxeCodes[indexNextCode];
    }

    const updateExpenseStatus = (choice) => {
        if (!canLeaveCommentDraft()) return;
        if (choice.value === "Validated") {
            // XPEN-3375: second entry point into the same privileged call as the
            // Validate button below. The Select that reaches it is hardcoded
            // disabled today, so this is unreachable — gated anyway so re-enabling
            // that Select cannot ship an ungated path to validation. Scope note:
            // this guards validation only. The "Approved" and "Denied" branches
            // below are separate permissions (canApproveOrDeny) and are still
            // ungated; re-enabling the Select needs them handled too.
            if (!canValidate) return;
            validateExpense();
            return;
        }
        if (choice.value === "Approved") {
            approveExpense();
            return;
        }
        if (choice.value === "Denied") {
            setShowDisapproval(true);
            return;
        }
        setExpense(prev => ({ ...prev, status: choice.value }));
        schedulePersist({ immediate: true });
    };

    const approveExpense = () => {
        if (!canLeaveCommentDraft()) return;
        setExpense(prev => ({ ...prev, status: 'Approved' }));
        setAction('approving');
        schedulePersist({ immediate: true });
    }

    const disApproveExpense = () => {
        if (!canLeaveCommentDraft()) return;
        setAction('disApproving');
        setExpense(prev => ({
            ...prev,
            status: 'Denied',
            status_details: new Date().toLocaleString() + ' : ' + data.firstName + ' ' + data.lastName + ' : ' + causeDenial + '\n' + expense?.status_details
        }
        ));
        setShowDisapproval(false);
        schedulePersist({ immediate: true });
    }

    const seedTravelTypeFrom = (isBillable) => [1, true].includes(isBillable) ? 'billable' : 'inter_office';

    const hideBillableAndMatterForDualInterOffice = () =>
        expense.json?.type === 'mileage' && !isMileageBillableScope();

    const isDualTravelBillableMileage =
        expense.json?.type === 'mileage' && isMileageBillableScope();

    // XPEN-2866: Matter needs Client scope, not Billable scope — a Client + Non-billable
    // trip still needs a Matter, so this must not reuse isDualTravelBillableMileage.
    const travelTypeNeedsMatter =
        expense.json?.type === 'mileage' && resolveMileageMatterRequirement(expense.json, resolvedMileageTravelType);

    const mileageType1Required =
        deferDualTravelPersist() && expense.json?.type === 'mileage' && isMileageOfficeTravel();
    const mileageAddressRouteRequired =
        deferDualTravelPersist() && expense.json?.type === 'mileage' && !isMileageOfficeTravel() && !isMileageOpenAmount();
    const mileageOpenAmountRequired =
        deferDualTravelPersist() && expense.json?.type === 'mileage' && isMileageOpenAmount();
    const mileageType2Required = deferDualTravelPersist() && isDualTravelBillableMileage;
    const mileageMatterRequired = deferDualTravelPersist() && travelTypeNeedsMatter;

    // Belt-and-suspenders: keys are filtered in handleMileageSave and on travel_type change.
    const visibleMileageValidationErrorKeys = useMemo(() => {
        if (expense.json?.travel_type_id && resolvedMileageTravelType) {
            return filterMileageErrorKeysForResolvedTravelType(
                mileageValidationErrorKeys,
                resolvedMileageTravelType
            );
        }
        return filterMileageErrorKeysForTravelType(
            mileageValidationErrorKeys,
            expense.json?.travel_type
        );
    }, [
        expense.json?.travel_type,
        expense.json?.travel_type_id,
        mileageValidationErrorKeys,
        resolvedMileageTravelType,
    ]);

    const handleMileageTravelTypeSelect = (choice) => {
        if (saving || validating) return;
        setSavingError(null);
        setMileageValidationErrorKeys([]);
        const nextTravelType = choice?.value;
        if (!nextTravelType) return;
        const current = expense.json?.travel_type ?? 'inter_office';
        if (nextTravelType === current) return;
        setExpense(prev => {
            const j = { ...prev.json, travel_type: nextTravelType };
            // Legacy address strings stay as '' (rest of form reads them as strings); office ids are new optional keys—delete so the payload omits them.
            j.AddressOrigin = '';
            j.AddressDestination = '';
            delete j.departure_office_id;
            delete j.destination_office_id;
            clearMileageCalculatedAmounts(j);
            if (nextTravelType === 'inter_office') {
                j.IsBillable = false;
                j.MatterId = '';
                const listItems = (j.ListItem || []).map(item => ({
                    ...item,
                    IsBillable: false,
                    MatterId: '',
                }));
                j.ListItem = listItems;
            } else if (nextTravelType === 'billable') {
                j.IsBillable = true;
                j.MatterId = '';
                const homeAddrRaw = profileHomeOfficeAddressRef.current;
                const homeAddr =
                    homeAddrRaw != null && String(homeAddrRaw).trim() !== '' ? String(homeAddrRaw) : '';
                if (homeAddr) {
                    j.AddressOrigin = homeAddr;
                }
                j.ListItem = (j.ListItem || []).map((item) => ({
                    ...item,
                    IsBillable: 1,
                    MatterId: '',
                }));
            }
            return { ...prev, json: j };
        });
        schedulePersist();
    };

    const applyMileageTravelTypeImputation = useCallback((json, selectedTravelType) => {
        if (!selectedTravelType) {
            return json;
        }
        const linkedImputationId = selectedTravelType.imputation_id != null
            ? String(selectedTravelType.imputation_id)
            : '';
        if (!linkedImputationId) {
            return {
                ...json,
                ImputationLevel1Id: null,
                ImputationLeafId: null,
            };
        }

        const linkedRow = imputations.find(
            (row) => imputationDocumentId(row) === linkedImputationId
        );
        if (!linkedRow) {
            return {
                ...json,
                ImputationLevel1Id: null,
                ImputationLeafId: null,
            };
        }

        const linkedLeafRow = linkedRow.parent_id ? linkedRow : null;
        const linkedLevel1Row = linkedLeafRow
            ? (imputations.find((row) => imputationDocumentId(row) === String(linkedLeafRow.parent_id)) ?? null)
            : linkedRow;
        const categoryRow = linkedLevel1Row ?? linkedLeafRow;
        const level1Id = linkedLevel1Row ? imputationDocumentId(linkedLevel1Row) : null;
        const leafId = linkedLeafRow ? imputationDocumentId(linkedLeafRow) : null;
        const patchedListItems = (json.ListItem || []).map((item) =>
            applyImputationCodingToListItem(item, json, {
                level1Row: linkedLevel1Row,
                leafRow: linkedLeafRow,
                preserveExistingWhenEmpty: true,
                nonBillableCostType: nonBillableCostTypeOf(data),
            })
        );

        let nextJson = {
            ...json,
            Category: categoryRow?.category ?? json.Category,
            ImputationLevel1Id: level1Id,
            ImputationLeafId: leafId,
            ListItem: patchedListItems,
        };

        const imputationTaxCode = resolveImputationTaxCodeFromRows({
            level1Row: linkedLevel1Row,
            leafRow: linkedLeafRow,
        });
        if (imputationTaxCode) {
            nextJson = applyTaxCodeToExpenseJson(nextJson, imputationTaxCode);
        }
        return nextJson;
    }, [imputations]);

    const applyMileageTravelTypeSwitch = useCallback((json, previousTravelType, nextTravelType, nextTravelTypeId, sessionBaseline = null) => {
        if (!nextTravelType) {
            return json;
        }

        const nextTravelLegacyMode = nextTravelType?.billable_scope === 'billable' ? 'billable' : 'inter_office';
        const previousTravelMode = previousTravelType?.travel_mode;
        const nextTravelMode = nextTravelType?.travel_mode;
        const preserveMatter = travelTypeRequiresMatter(previousTravelType) && travelTypeRequiresMatter(nextTravelType);
        const preserveOtherTravelRoute =
            previousTravelMode === 'other_travel' && nextTravelMode === 'other_travel';
        const preserveOfficeTravelRoute =
            previousTravelMode === 'office_travel' && nextTravelMode === 'office_travel';
        const routePreserved = preserveOtherTravelRoute || preserveOfficeTravelRoute;
        const existingMatter = resolveMatterIdFromJson(json);

        const nextJson = {
            ...json,
            travel_type: nextTravelLegacyMode,
            travel_type_id: nextTravelTypeId,
        };

        if (nextTravelMode === 'open_amount') {
            // XPEN-3425: an Open Amount type has no rate, by construction — checked
            // ahead of the rate-copy branch so a rate on the config could never win.
            // nextJson is built by spreading the previous json, so without this the
            // previous type's rate would silently survive on screen.
            delete nextJson.rate;
        } else if (nextTravelType?.rate != null) {
            nextJson.rate = nextTravelType.rate;
        }

        if (nextTravelMode !== 'open_amount') {
            // Symmetric to the rate clear above, but NOT the same mechanism: the
            // save is a $mergeObjects merge (XPEN-3421), so an omitted key keeps
            // whatever is already stored — unlike rate, nothing server-side
            // re-derives open_amount on every save, so deleting the key here
            // would leave a stale stored amount. Write an explicit empty value,
            // the same pattern XPEN-3421 uses for Distance/isRoundTrip/AddressOrigin.
            nextJson.open_amount = '';
        }

        // Keep route fields only when both travel types share the same travel_mode.
        if (!preserveOtherTravelRoute && !preserveOfficeTravelRoute) {
            nextJson.AddressOrigin = '';
            nextJson.AddressDestination = '';
            delete nextJson.departure_office_id;
            delete nextJson.destination_office_id;
        } else if (preserveOtherTravelRoute) {
            delete nextJson.departure_office_id;
            delete nextJson.destination_office_id;
        }

        const nextIsBillable = nextTravelType?.billable_scope === 'billable';
        if (nextIsBillable) {
            nextJson.IsBillable = true;
            if (preserveMatter && existingMatter) {
                nextJson.MatterId = existingMatter;
            } else if (!preserveMatter) {
                nextJson.MatterId = '';
            }
            nextJson.ListItem = (nextJson.ListItem || []).map((item) => ({
                ...item,
                IsBillable: 1,
                MatterId: preserveMatter ? (existingMatter || item.MatterId || '') : '',
            }));
        } else {
            nextJson.IsBillable = false;
            if (!preserveMatter) {
                nextJson.MatterId = '';
            } else if (existingMatter) {
                nextJson.MatterId = existingMatter;
            }
            nextJson.ListItem = (nextJson.ListItem || []).map((item) => ({
                ...item,
                IsBillable: false,
                MatterId: preserveMatter ? (existingMatter || item.MatterId || '') : '',
            }));
        }

        if (!routePreserved) {
            clearMileageCalculatedAmounts(nextJson);
        }
        return restoreMileageSessionBaselineFields(nextJson, nextTravelType, sessionBaseline);
    }, []);

    const applyImputationTaxCodeToExpense = (baseExpense, level1Row, leafRow) => {
        const taxCode = resolveImputationTaxCodeFromRows({ level1Row, leafRow });
        if (!taxCode) {
            setExpense(baseExpense);
            schedulePersist();
            return;
        }

        const expenseWithTax = {
            ...baseExpense,
            json: applyTaxCodeToExpenseJson(baseExpense.json, taxCode),
        };

        if (expenseWithTax.json.ListItem?.length === 1) {
            handleExpenseTaxCodeChange(expenseWithTax, taxCode);
            return;
        }

        // Multi-line: update TaxCode per row only; per-line tax amounts stay as entered.
        setExpense(expenseWithTax);
        schedulePersist();
    };

    const updateCategory = (choice) => {
        const imputation = imputations.find(x => !x.parent_id && x.category === choice.value);
        if (
            !imputation &&
            staleImputationRoot &&
            choice?.value === staleImputationRoot.category
        ) {
            return;
        }
        setStaleImputationRoot(null);
        setStaleImputationLeaf(null);

        const updated_expense = {
            ...expense,
            json: {
                ...expense.json,
                Category: choice.value,
                ImputationLevel1Id: imputation ? imputationDocumentId(imputation) : null,
                ImputationLeafId: null,
                ...(expense.json.ListItem && imputation && {
                    ListItem: expense.json.ListItem.map((item) =>
                        applyImputationCodingToListItem(item, expense.json, {
                            level1Row: imputation,
                            leafRow: null,
                            nonBillableCostType: nonBillableCostTypeOf(data),
                        })
                    ),
                }),
            },
        };

        setCategoryEdited(true);
        applyImputationTaxCodeToExpense(updated_expense, imputation, null);
    };

    const updateSubcategory = (choice) => {
        if (
            staleImputationLeaf &&
            choice?.value &&
            String(choice.value) === imputationDocumentId(staleImputationLeaf)
        ) {
            return;
        }
        setStaleImputationLeaf(null);
        const leafId = choice ? choice.value : null;
        const leafRow = leafId
            ? imputations.find((row) => imputationDocumentId(row) === String(leafId))
            : null;

        const updated_expense = {
            ...expense,
            json: {
                ...expense.json,
                ImputationLeafId: leafId,
                ...(expense.json.ListItem && {
                    ListItem: expense.json.ListItem.map((item) =>
                        applyImputationCodingToListItem(item, expense.json, {
                            level1Row: imputationL1Row,
                            leafRow,
                            nonBillableCostType: nonBillableCostTypeOf(data),
                        })
                    ),
                }),
            },
        };

        applyImputationTaxCodeToExpense(updated_expense, imputationL1Row, leafRow);
    };



    // XPEN-3187: safe no-op if already cached by App.js's session-level fetch.
    useEffect(() => {
        loadXpensCategories(data);
    }, [data]);

    const categoryLabel = (category) => getXpensCategoryLabel(data, category);
    const { l1Row: imputationL1Row, l2Row: imputationL2Row } = useMemo(
        () => resolveDisplayImputationRows(imputations, expense.json ?? {}),
        [imputations, expense.json]
    );

    // XPEN-3252: no client category resolved means nothing to show — the AI's raw
    // guess (expense.json.Category) is not the client's choice and must not be
    // displayed as though it were one. json.Category itself stays persisted
    // unchanged (submit, and the separate conditional-fields.js Guests/Location
    // consumers, still read it) — only this display falls back to null here,
    // same as subcategoryValue below when imputationL2Row is absent.
    const categoryValue = useMemo(() => {
        if (imputationL1Row) {
            return { value: imputationL1Row.category, label: imputationDisplayLabel(imputationL1Row, data) };
        }
        const l1 = expense.json?.ImputationLevel1Id;
        if (
            staleImputationRoot &&
            l1 &&
            imputationDocumentId(staleImputationRoot) === String(l1)
        ) {
            return {
                value: staleImputationRoot.category,
                label: deletedImputationOptionLabel(
                    staleImputationRoot,
                    data.t.read('review_expense.imputation_deleted'),
                    data
                ),
                isDeleted: true,
            };
        }
        return null;
    }, [imputationL1Row, staleImputationRoot, expense.json?.ImputationLevel1Id, data, data.t.lang, data.xpensCategories])


    const categoryOptions = useMemo(() => {
        const naOption = { value: data.t.read("n_a"), label: data.t.read("n_a") };
        const rootOptions = [...imputations]
            .filter(imp => !imp.parent_id)
            .sort((a, b) => compareDisplayLabelStrings(
                categoryLabel(a.category),
                categoryLabel(b.category),
                data.t.lang
            ))
            .map((imputation) => ({
                value: imputation.category,
                label: categoryLabel(imputation.category),
            }));
        const l1 = expense.json?.ImputationLevel1Id;
        if (
            staleImputationRoot &&
            l1 &&
            imputationDocumentId(staleImputationRoot) === String(l1) &&
            !rootOptions.some((option) => option.value === staleImputationRoot.category)
        ) {
            rootOptions.unshift({
                value: staleImputationRoot.category,
                label: deletedImputationOptionLabel(
                    staleImputationRoot,
                    data.t.read('review_expense.imputation_deleted'),
                    data
                ),
                isDeleted: true,
            });
        }
        return [naOption, ...rootOptions];
    }, [imputations, categoryLabel, data, data.t.lang, data.xpensCategories, staleImputationRoot, expense.json?.ImputationLevel1Id]);

    const subcategoryOptions = useMemo(() => {
        if (!imputationL1Row) return [];
        const l1Id = imputationDocumentId(imputationL1Row);
        const options = sortImputationRowsByDisplayLabel(
            imputations.filter(r => r.parent_id && imputationDocumentId({ _id: r.parent_id }) === l1Id),
            data.t.lang,
            data
        ).map(r => ({
            value: imputationDocumentId(r),
            label: imputationDisplayLabel(r, data)
        }));
        const leafId = expense.json?.ImputationLeafId;
        if (
            staleImputationLeaf &&
            leafId &&
            imputationDocumentId(staleImputationLeaf) === String(leafId) &&
            !options.some((option) => option.value === String(leafId))
        ) {
            options.unshift({
                value: imputationDocumentId(staleImputationLeaf),
                label: deletedImputationOptionLabel(
                    staleImputationLeaf,
                    data.t.read('review_expense.imputation_deleted'),
                    data
                ),
                isDeleted: true,
            });
        }
        return options;
    }, [imputations, imputationL1Row, data, data.t.lang, data.xpensCategories, staleImputationLeaf, expense.json?.ImputationLeafId]);

    const subcategoryValue = useMemo(() => {
        if (imputationL2Row) {
            return {
                value: imputationDocumentId(imputationL2Row),
                label: imputationDisplayLabel(imputationL2Row, data)
            };
        }
        const leafId = expense.json?.ImputationLeafId;
        if (
            staleImputationLeaf &&
            leafId &&
            imputationDocumentId(staleImputationLeaf) === String(leafId)
        ) {
            return {
                value: imputationDocumentId(staleImputationLeaf),
                label: deletedImputationOptionLabel(
                    staleImputationLeaf,
                    data.t.read('review_expense.imputation_deleted'),
                    data
                ),
                isDeleted: true,
            };
        }
        return null;
    }, [imputationL2Row, staleImputationLeaf, expense.json?.ImputationLeafId, data, data.t.lang, data.xpensCategories]);

    const formatDeletedImputationLabel = (option) =>
        formatImputationSelectOptionLabel(
            option,
            data.t.read('review_expense.imputation_deleted')
        );

    const mileageTravelTypeOptions = useMemo(() => ([
        { value: 'inter_office', label: data.t.read('mileage_travel_type.inter_office') },
        { value: 'billable', label: data.t.read('mileage_travel_type.billable') },
    ]), [data]);

    const mileageTravelTypeValue = useMemo(() => {
        const v = expense.json?.travel_type ?? 'inter_office';
        return mileageTravelTypeOptions.find((o) => o.value === v) || mileageTravelTypeOptions[0];
    }, [expense.json?.travel_type, mileageTravelTypeOptions]);

    const type1DepartureSelectValue = useMemo(() => {
        const id = expense.json?.departure_office_id;
        const sid = imputationDocumentId({ _id: id });
        if (!sid) return null;
        return (
            pobOfficeOptions.find((o) => o.value === sid) || {
                value: sid,
                label: data.t.read('mileage_type1.office_not_in_list'),
                raw: null,
            }
        );
    }, [data, expense.json?.departure_office_id, pobOfficeOptions]);

    const type1DestinationSelectValue = useMemo(() => {
        const id = expense.json?.destination_office_id;
        const sid = imputationDocumentId({ _id: id });
        if (!sid) return null;
        return (
            pobOfficeOptions.find((o) => o.value === sid) || {
                value: sid,
                label: data.t.read('mileage_type1.office_not_in_list'),
                raw: null,
            }
        );
    }, [data, expense.json?.destination_office_id, pobOfficeOptions]);

    const persistMileageDistanceWhenRouteComplete = useCallback((json) => {
        if (json?.type !== 'mileage') {
            return;
        }
        if (!hasCompleteMileageRoute(json)) {
            return;
        }
        const depId = imputationDocumentId({ _id: json.departure_office_id });
        const destId = imputationDocumentId({ _id: json.destination_office_id });
        if (depId && destId && depId === destId) {
            return;
        }
        if (saving || validating) {
            return;
        }
        setSaving(true);
    }, [data, saving, validating]);

    const handleType1OfficeSelect = useCallback((role, choice) => {
        if (saving || validating) return;
        setSavingError(null);
        if (!choice || !choice.raw) {
            setExpense((prev) => {
                const j = { ...prev.json };
                if (role === 'departure') {
                    delete j.departure_office_id;
                    j.AddressOrigin = '';
                } else {
                    delete j.destination_office_id;
                    j.AddressDestination = '';
                }
                clearMileageCalculatedAmounts(j);
                return { ...prev, json: j };
            });
            schedulePersist();
            return;
        }
        const office = choice.raw;
        const id = imputationDocumentId({ _id: office._id });
        const addr = office.address != null && office.address !== '' ? String(office.address) : '';
        const prevJson = expenseJsonRef.current ?? expense.json;
        const nextOrigin =
            role === 'departure' ? addr : String(prevJson?.AddressOrigin ?? '').trim();
        const nextDest =
            role === 'destination' ? addr : String(prevJson?.AddressDestination ?? '').trim();
        setExpense((prev) => {
            const j = {
                ...prev.json,
                ...(role === 'departure'
                    ? { departure_office_id: id, AddressOrigin: addr }
                    : { destination_office_id: id, AddressDestination: addr }),
            };
            clearMileageCalculatedAmounts(j);
            const listItems = (j.ListItem || []).map((item) => ({
                ...item,
                IsBillable: false,
                MatterId: '',
            }));
            return {
                ...prev,
                json: {
                    ...j,
                    IsBillable: false,
                    MatterId: '',
                    ListItem: listItems,
                },
            };
        });
        persistMileageDistanceWhenRouteComplete({
            ...prevJson,
            type: 'mileage',
            ...(role === 'departure'
                ? { departure_office_id: id, AddressOrigin: addr }
                : { destination_office_id: id, AddressDestination: addr }),
            AddressOrigin: nextOrigin,
            AddressDestination: nextDest,
        });
    }, [persistMileageDistanceWhenRouteComplete, saving, validating]);

    useEffect(() => {
        if (expense.json?.type !== 'mileage') return;
        if (!isMileageOfficeTravel()) return;
        if (mileageSessionBaselineRef.current?.officeTravel?.departure_office_id) return;
        const dep = expense.json?.departure_office_id;
        if (dep != null && dep !== '') return;
        if (type1HomePrefillAppliedForChecksumRef.current === expense.checksum) return;
        if (saving || validating) return;
        const profileHomeRaw = expensesAccount?.user?.home_office_id ?? data.home_office_id;
        const homeId = imputationDocumentId({ _id: profileHomeRaw });
        if (!homeId) return;
        if (pobOfficeOptions.length === 0) return;
        const opt = pobOfficeOptions.find((o) => o.value === homeId);
        if (!opt?.raw) return;
        handleType1OfficeSelect('departure', opt);
        type1HomePrefillAppliedForChecksumRef.current = expense.checksum;
    }, [
        data.home_office_id,
        expensesAccount?.user?.home_office_id,
        expense.checksum,
        expense.json?.departure_office_id,
        expense.json?.travel_type,
        expense.json?.travel_type_id,
        expense.json?.type,
        handleType1OfficeSelect,
        pobOfficeOptions,
        resolvedMileageTravelType,
        saving,
        validating,
    ]);

    // XPEN-2509: when offices load after the user is already on billable other_travel, prefill departure once per checksum (handler also prefills when switching).
    useEffect(() => {
        if (expense.json?.type !== 'mileage') return;
        if (isMileageOfficeTravel()) return;
        // XPEN-3425: an Open Amount trip has no route to prefill — without this, a
        // billable Open Amount trip would silently acquire the user's home address
        // and persist it, then be refused at validation as internally inconsistent.
        if (isMileageOpenAmount()) return;
        if (!isMileageBillableScope()) return;
        if (mileageSessionBaselineRef.current?.otherTravel?.AddressOrigin) return;
        const origin = String(expense.json?.AddressOrigin ?? '').trim();
        if (origin) return;
        const homeAddr = String(profileHomeOfficeAddressRef.current ?? '').trim();
        if (!homeAddr) return;
        if (type2HomePrefillAppliedForChecksumRef.current === expense.checksum) return;
        if (saving || validating) return;
        setExpense((prev) => ({
            ...prev,
            json: { ...prev.json, AddressOrigin: homeAddr },
        }));
        type2HomePrefillAppliedForChecksumRef.current = expense.checksum;
        schedulePersist();
    }, [
        data.home_office_id,
        expensesAccount?.user?.home_office_id,
        expense.checksum,
        expense.json?.AddressOrigin,
        expense.json?.travel_type,
        expense.json?.travel_type_id,
        expense.json?.type,
        profileHomeOfficeAddress,
        resolvedMileageTravelType,
        saving,
        validating,
    ]);

    // Sync MatterId between header and first line item when only one side was persisted (creation flow).
    useEffect(() => {
        if (expense.json?.type !== 'mileage') return;
        if (!resolveMileageMatterRequirement(expense.json, resolvedMileageTravelType)) return;
        if (saving || validating) return;
        const synced = syncMatterIdFromJson(expense.json ?? {});
        if (!resolveMatterIdFromJson(synced)) return;
        if (
            expense.json?.MatterId === synced.MatterId
            && expense.json?.ListItem?.[0]?.MatterId === synced.ListItem?.[0]?.MatterId
        ) {
            return;
        }
        setExpense((prev) => ({
            ...prev,
            json: syncMatterIdFromJson(prev.json ?? {}),
        }));
    }, [
        expense.checksum,
        expense.json?.ListItem?.[0]?.MatterId,
        expense.json?.ListItem?.[0]?.MatterID,
        expense.json?.MatterId,
        expense.json?.travel_type,
        expense.json?.travel_type_id,
        expense.json?.type,
        resolvedMileageTravelType,
        saving,
        validating,
    ]);

    // XPEN-2550: switching travel type must drop errors from the previous mode (e.g. Type 1 office → Type 2 route).
    useEffect(() => {
        if (expense.json?.type !== 'mileage') return;
        if (expense.json?.travel_type_id && resolvedMileageTravelType) {
            setMileageValidationErrorKeys((prev) =>
                filterMileageErrorKeysForResolvedTravelType(prev, resolvedMileageTravelType)
            );
            return;
        }
        setMileageValidationErrorKeys((prev) =>
            filterMileageErrorKeysForTravelType(prev, expense.json?.travel_type)
        );
    }, [data, expense.json?.travel_type, expense.json?.travel_type_id, expense.json?.type, resolvedMileageTravelType]);

    // XPEN-2548: legacy mileage without travel_type_id — seed travel_type once from IsBillable.
    useEffect(() => {
        if (expense.json?.type !== 'mileage') return;
        if (expense.json?.travel_type_id) return;
        if (expense.json?.travel_type != null) return;
        if (travelTypeSeedAppliedForChecksumRef.current === expense.checksum) return;
        if (saving || validating) return;
        const seedTravel = seedTravelTypeFrom(expense.json?.IsBillable);
        travelTypeSeedAppliedForChecksumRef.current = expense.checksum;
        setExpense((prev) => ({
            ...prev,
            json: { ...prev.json, travel_type: seedTravel },
        }));
        schedulePersist();
    }, [
        expense.checksum,
        expense.json?.IsBillable,
        expense.json?.travel_type,
        expense.json?.travel_type_id,
        expense.json?.type,
        saving,
        validating,
    ]);

    const applyTipRecalcForSingleItem = ({
        bigTotal,
        tip,
        taxCode,
        updatedExpenseBase,
    }) => {
        if (!Number.isFinite(bigTotal) || bigTotal <= 0) return false;

        const itemTotal = bigTotal - tip;
        const updatedItem = {
            ...updatedExpenseBase.json.ListItem[0],
            Total: itemTotal,
            TaxCode: defaultTaxCode,
        };
        const recalculatedExpense = {
            ...updatedExpenseBase,
            json: {
                ...updatedExpenseBase.json,
                ListItem: [updatedItem],
                Tax1: 0,
                Tax2: 0,
                Total: itemTotal,
                Tax: 0,
                TotalWithTax: itemTotal,
                BigTotalPayed: itemTotal + tip
            }
        };

        handleExpenseTaxCodeChange(recalculatedExpense, taxCode);
        return true;
    };

    const handleUnadjustedTotalChange = (val) => {
        const expenseImputation = findImputationRowForExpenseEditor(imputations, expense?.json);
        const taxCode = expenseImputation?.tax_code || defaultTaxCode;
        const tip = Number(expense.json.Tip) || 0;

        const updatedExpense = {
            ...expense,
            json: { ...expense.json, UnadjustedTotal: val, TaxCode: defaultTaxCode }
        };

        if (updatedExpense.json.ListItem?.length === 1) {
            const applied = applyTipRecalcForSingleItem({
                bigTotal: Number(val),
                tip,
                taxCode,
                updatedExpenseBase: updatedExpense,
            });
            if (applied) return;
        }

        setExpense(prev => ({
            ...prev,
            json: { ...prev.json, Total: val - tip, UnadjustedTotal: val }
        }));
        schedulePersist();
    }
    
    const handleTipChange = (val) => {
        const prevTip = expense.json.Tip || 0;
        if(prevTip === 0){
            if (expense.json.ListItem?.length === 1) {
                const tip = Number(val) || 0;
                const taxCode = expense.json.TaxCode || defaultTaxCode;
                const bigTotalPayed = Number(expense.json.BigTotalPayed);
                const updatedExpense = {
                    ...expense,
                    json: { ...expense.json, Tip: tip, TaxCode: defaultTaxCode }
                };

                const applied = applyTipRecalcForSingleItem({
                    bigTotal: bigTotalPayed,
                    tip,
                    taxCode,
                    updatedExpenseBase: updatedExpense,
                });
                if (applied) return;
            }

            setExpense(prev => ({ ...prev, json: { ...prev.json, Tip: val } }));
            schedulePersist()
            return;
        }

        setExpense(prev => ({ ...prev, json: { ...prev.json, Tip: val } }));
        schedulePersist()
    }

    const handleExpenseTaxCodeChange = (updatedExpense = {...expense}, selectedTaxCode = null) => {
        if (!updatedExpense?.json?.ListItem || updatedExpense.json.ListItem.length !== 1) return;

        const toNumber = (value, fallback = 0) => {
            const n = Number(value);
            return Number.isFinite(n) ? n : fallback;
        };
        const [originalItem] = updatedExpense.json.ListItem;
        const item = { ...originalItem };

        // Use next tax code
        const taxCode = selectedTaxCode ?? getNextTaxCode(item.TaxCode ?? defaultTaxCode);


        // Normalize numeric inputs
        const bigTotalPayed = toNumber(updatedExpense.json.BigTotalPayed, 0);
        const tip = toNumber(updatedExpense.json.Tip, 0);


        const taxDeduction = toNumber(updatedExpense.json.TaxDeduction, 0);

        // Tax rates for the *new* tax code
        const [rawT1 = 0, rawT2 = 0, rawT3 = 0] = taxesPercentage[taxCode] ?? [];
        const t1 = toNumber(rawT1, 0);
        const t2 = toNumber(rawT2, 0);
        const t3 = toNumber(rawT3, 0);
        const taxP = t1 + t2 + t3;

        // Amount actually paid, before tip
        const totalPreTip = bigTotalPayed - tip;
        if (!Number.isFinite(totalPreTip) || totalPreTip <= 0) {
            setExpense({
                ...updatedExpense,
                json: {
                    ...updatedExpense.json,
                    ListItem: [{ ...item, TaxCode: taxCode }],
                    TaxCode: taxCode,
                },
            });
            // XPEN-2872: zero-total receipts still auto-save the tax code / category change
            // (no-op for mileage, which defers to manual Save).
            schedulePersist();
            return;
        }

        // Base before tax
        const totalPreTax = taxP !== 0 ? totalPreTip / (1 + taxP) : totalPreTip;

        // Full tax on that base
        const fullTax = totalPreTax * taxP;

        // NON-recoverable tax (goes inside item.Total)
        const nonRecTax = fullTax * taxDeduction;

        // Item total = base + non-recoverable tax
        const itemTotal = totalPreTax + nonRecTax;

        item.Total = itemTotal;
        item.TaxCode = taxCode;

        computeTaxes({
            ...updatedExpense,
            json: {
                ...updatedExpense.json,
                ListItem: [item],
                TaxCode: taxCode,
                Total: itemTotal,
            },
        });
    }

    // PDFVisualizer shows the paperclip whenever ownerType+ownerId are set —
    // without a real checksum that would list account-wide attachments (list
    // omits owner_sub_id). Gate all three owner props together.
    const attachmentsOwnerReady = Boolean(account_id && expense?.checksum);

    return expense == null ?
        <div className="loading-container">
            <FontAwesomeIcon className="infinite-rotate" icon={faSpinner} />
        </div>
        :
        <div ref={commentRoot} className="invoice bg-white">
            {
                // account_id is an expenses-account id, not an invoice id, so
                // showInvoiceExports is off — the export menu items call an
                // invoice-only backend route that would always fail otherwise.
                // Always mounted (not gated on pdfBuff) — PDFVisualizer owns the
                // loaded/loading/unavailable states via pdfBuff+isLoading, so a
                // failed fetch shows its own message instead of the panel
                // vanishing, and paging between receipts doesn't reflow the layout.
                // XPEN-3007 — attachments are per document (this receipt), not
                // grouped across the whole expense account. ownerSubId=checksum
                // keeps files tied to this receipt for later submit transfer.
                !data.isMobile && expense.json?.type !== 'mileage' &&
                <PDFVisualizer
                    pdfBuff={pdfBuff}
                    // Match the invoice split so resizing leaves room for expense fields.
                    minWidth="25vw"
                    maxWidth="45vw"
                    data={data}
                    downloadFile={downloadFile}
                    // account_id alone is shared by every receipt in the account —
                    // checksum is what actually identifies this one document (same
                    // pairing already used for the PDF's own path, above).
                    id={`${account_id}_${expense.checksum}`}
                    showInvoiceExports={false}
                    primaryTabLabel={data.t.read("document_tab_label")}
                    filenamePrefix="expense"
                    isLoading={isLoadingPdf}
                    ownerType={attachmentsOwnerReady ? "expense" : null}
                    ownerId={attachmentsOwnerReady ? account_id : null}
                    ownerSubId={attachmentsOwnerReady ? expense.checksum : null}
                    attachmentsCount={attachmentsCount}
                    pdfView={pdfView}
                    changePdfView={setPdfView}
                />
            }
            {
                <div className={data.isMobile ? "content-section d-flex flex-column mb-5" : "content-section d-flex flex-column"}
                    style={data.isMobile ? undefined : { minWidth: '35vh', overflowX: 'auto' }}>
                    <div className="d-flex main-tabs no-scrollbar" style={{ maxWidth: '100%', overflowX: 'auto', flexShrink: 0 }}>
                        {
                            expensesAccount?.expenses && expensesAccount?.expenses?.length > 1 &&
                            <div className='pointer icon-box'>
                                <FontAwesomeIcon data-testid="expense-nav-prev" disabled={saving} icon={faAngleLeft} className="fa-2x pointer" color='#F39800' onClick={() => getNextExpense(false, false)} />
                            </div>
                        }
                        <div className={"flex-grow-1 p-3 tab-title " + (currentView === "data" ? "tab-selected" : "")} onClick={() => setCurrentView("data")}>
                            {data.t.read("data")}
                        </div>
                        <div
                            className="p-3 pointer"
                            onClick={handleCloseExpense}
                            data-testid="expense-close"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleCloseExpense();
                                }
                            }}
                        >
                            <img src={closeIcon2} alt="Close" />
                        </div>
                        {
                            expensesAccount?.expenses && expensesAccount?.expenses?.length > 1 &&
                            <div className='pointer icon-box'>
                                <FontAwesomeIcon data-testid="expense-nav-next" disabled={saving} icon={faAngleRight} className="fa-2x pointer" color='#F39800' onClick={() => getNextExpense(false, true)} />
                            </div>
                        }
                    </div>
                    {data.isMobile && expense.json?.type !== 'mileage' &&
                        <PdfImageDisplay file={pdfBuff} isLoading={isLoadingPdf} />
                    }
                    {/* Keep desktop fields scrolling independently of the PDF and tabs;
                        mobile retains the existing single, stacked scrolling surface. */}
                    <div style={data.isMobile ? undefined : { overflowY: 'auto', maxHeight: 'calc(100vh - 150px)' }}>
                    <div className="p-4 d-flex flex-column justify-content-between flex-grow-1">
                        <div>
                            <ExpenseFormItem data={data} label={data.t.read("invoice.status_txt")} show={true}>
                                <Select
                                    name="status"
                                    value={expense?.status ? { value: expense.status, label: data.t.read(`status.${expense.status.toLowerCase()}`) } : null}
                                    options={data.config.invoice_status.map(s => ({ label: data.t.read(`status.${s.toLowerCase()}`), value: s }))}
                                    styles={styleSelect}
                                    isDisabled={true}
                                    className="pl-1"
                                    classNamePrefix="select"
                                    onChange={updateExpenseStatus}
                                />
                            </ExpenseFormItem>

                            {expense.json?.type === 'mileage' ? (
                                    <>
                                        <ExpenseFormItem data={data} label={data.t.read('mileage_travel_type.label')} show={true} requiredMark={!expense.json?.travel_type_id && deferDualTravelPersist()}>
                                            <div data-testid="mileage-travel-type-section">
                                                {expense.json?.travel_type_id ? (() => {
                                                    const activeTypes = (rates?.mileage_travel_types ?? []).filter(t => t.active);
                                                    const selectedTT = activeTypes.find(t => t.id === expense.json.travel_type_id);
                                                    // XPEN-3481: offer only the travel types this report's owner may pick,
                                                    // but keep resolving the stored one from the full list — a trip already
                                                    // saved against a restricted type must still show its name rather than
                                                    // going blank on the person who has to correct it.
                                                    // Offer nothing while the answer is still coming: the
                                                    // alternative is briefly offering types the owner may not
                                                    // use. The stored one is resolved separately, so the field
                                                    // still shows what the trip already carries.
                                                    const selectableTypes = accessibleTravelTypeIdsLoading
                                                        ? []
                                                        : filterToAccessibleTravelTypes(activeTypes, accessibleTravelTypeIds);
                                                    return (
                                                    <Select
                                                        inputId="mileage-travel-type-id"
                                                        name="mileage_travel_type_id"
                                                        value={selectedTT ? { value: selectedTT.id, label: selectedTT.name } : null}
                                                        options={selectableTypes.map(t => ({ value: t.id, label: t.name }))}
                                                        styles={styleSelect}
                                                        className="pl-1"
                                                        classNamePrefix="select"
                                                        onChange={opt => {
                                                            const selectedTT = activeTypes.find(t => t.id === opt?.value);
                                                            if (!selectedTT) return;
                                                            const previousTT = activeTypes.find(t => t.id === expense.json?.travel_type_id);
                                                            setSavingError(null);
                                                            setMileageValidationErrorKeys([]);
                                                            const withTravelTypeSwitch = applyMileageTravelTypeSwitch(
                                                                expense.json ?? {},
                                                                previousTT,
                                                                selectedTT,
                                                                opt?.value ?? null,
                                                                mileageSessionBaselineRef.current
                                                            );
                                                            const nextJson = applyMileageTravelTypeImputation(
                                                                syncMatterIdFromJson(withTravelTypeSwitch),
                                                                selectedTT
                                                            );
                                                            setExpense((prev) => ({
                                                                ...prev,
                                                                json: nextJson,
                                                            }));
                                                            if (hasCompleteMileageRoute(nextJson)) {
                                                                persistMileageDistanceWhenRouteComplete(nextJson);
                                                            } else {
                                                                schedulePersist();
                                                            }
                                                        }}
                                                        isDisabled={fieldsLocked || validating}
                                                        placeholder={data.t.read('mileage_step1_travel_type') || 'Travel Type'}
                                                    />
                                                    );
                                                })() : (
                                                    <Select
                                                        inputId="mileage-travel-type"
                                                        name="mileage_travel_type"
                                                        value={mileageTravelTypeValue}
                                                        options={mileageTravelTypeOptions}
                                                        styles={styleSelect}
                                                        className="pl-1"
                                                        classNamePrefix="select"
                                                        onChange={handleMileageTravelTypeSelect}
                                                        isDisabled={fieldsLocked || validating}
                                                        placeholder={data.t.read('mileage_travel_type.label')}
                                                    />
                                                )}
                                            </div>
                                        </ExpenseFormItem>
                                        {/* XPEN-2508: Type 1 — POB office pickers only (XPEN-2500 AC); XPEN-2509 fills Type 2. */}
                                        <div data-testid="mileage-type1-fields-region">
                                            {isMileageOfficeTravel() ? (
                                                <>
                                                    {pobOfficeOptions.length === 0 && mileageType1EmptyHint === 'no_offices' ? (
                                                        <p className="text-muted small mb-2" data-testid="mileage-no-offices-hint">
                                                            {data.t.read('mileage_type1.no_offices_hint')}
                                                        </p>
                                                    ) : null}
                                                    {pobOfficeOptions.length === 0 && mileageType1EmptyHint === 'no_pob' ? (
                                                        <p className="text-muted small mb-2" data-testid="mileage-no-pob-hint">
                                                            {data.t.read('mileage_type1.no_pob_hint')}
                                                        </p>
                                                    ) : null}
                                                    <ExpenseFormItem data={data} label={data.t.read('mileage_type1.departure_office')} show requiredMark={mileageType1Required}>
                                                        <div data-testid="mileage-type1-departure-office">
                                                            <Select
                                                                name="mileage_type1_departure"
                                                                value={type1DepartureSelectValue}
                                                                options={pobOfficeOptions}
                                                                styles={styleSelect}
                                                                className="pl-1"
                                                                classNamePrefix="select"
                                                                onChange={(opt) => handleType1OfficeSelect('departure', opt)}
                                                                isDisabled={fieldsLocked || validating}
                                                                placeholder={data.t.read('mileage_type1.departure_office')}
                                                            />
                                                        </div>
                                                    </ExpenseFormItem>
                                                    <ExpenseFormItem data={data} label={data.t.read('mileage_type1.destination_office')} show requiredMark={mileageType1Required}>
                                                        <div data-testid="mileage-type1-destination-office">
                                                            <Select
                                                                name="mileage_type1_destination"
                                                                value={type1DestinationSelectValue}
                                                                options={pobOfficeOptions}
                                                                styles={styleSelect}
                                                                className="pl-1"
                                                                classNamePrefix="select"
                                                                onChange={(opt) => handleType1OfficeSelect('destination', opt)}
                                                                isDisabled={fieldsLocked || validating}
                                                                placeholder={data.t.read('mileage_type1.destination_office')}
                                                            />
                                                        </div>
                                                    </ExpenseFormItem>
                                                </>
                                            ) : null}
                                        </div>
                                        <div data-testid="mileage-type2-fields-region">
                                            {!isMileageOfficeTravel() && !isMileageOpenAmount() ? (
                                                <>
                                                    <ExpenseFormItem data={data} label={data.t.read("address_origin")} show requiredMark={mileageAddressRouteRequired}>
                                                        <div data-testid="mileage-type2-origin">
                                                            <AutoCompleteField
                                                                data={data}
                                                                required
                                                                value={expense.json?.AddressOrigin ?? ""}
                                                                onChange={(val) => {
                                                                    setExpense((prev) => {
                                                                        const j = { ...prev.json, AddressOrigin: val.name ?? val };
                                                                        clearMileageCalculatedAmounts(j);
                                                                        return { ...prev, json: j };
                                                                    });
                                                                    if (val?.name) {
                                                                        setSaving(true);
                                                                    }
                                                                }}
                                                                className="form-control border-0 p-0 mb-2"
                                                                searchUrl="/places/search/"
                                                                minLength={1}
                                                                onParentBlur={() => schedulePersist()}
                                                                placeholder={data.t.read("supplier_table.address")}
                                                            />
                                                        </div>
                                                    </ExpenseFormItem>
                                                    <ExpenseFormItem data={data} label={data.t.read("address_destination")} show requiredMark={mileageAddressRouteRequired}>
                                                        <div data-testid="mileage-type2-destination">
                                                            <AutoCompleteField
                                                                data={data}
                                                                required
                                                                value={expense.json?.AddressDestination ?? ""}
                                                                onChange={(val) => {
                                                                    setExpense((prev) => {
                                                                        const j = { ...prev.json, AddressDestination: val.name ?? val };
                                                                        clearMileageCalculatedAmounts(j);
                                                                        return { ...prev, json: j };
                                                                    });
                                                                    if (val?.name) {
                                                                        setSaving(true);
                                                                    }
                                                                }}
                                                                className="form-control border-0 p-0 mb-2"
                                                                searchUrl="/places/search/"
                                                                minLength={1}
                                                                onParentBlur={() => schedulePersist()}
                                                                placeholder={data.t.read("supplier_table.address")}
                                                            />
                                                        </div>
                                                    </ExpenseFormItem>
                                                </>
                                            ) : null}
                                        </div>
                                        {isMileageOpenAmount() ? (
                                            <ExpenseFormItem data={data} label={data.t.read('mileage_step1_amount')} show requiredMark={mileageOpenAmountRequired}>
                                                <div data-testid="mileage-open-amount-field">
                                                    <FormTextField
                                                        data-testid="mileage-open-amount-input"
                                                        format="money"
                                                        locked={fieldsLocked || validating}
                                                        value={expense.json?.open_amount ? expense.json?.open_amount : ""}
                                                        validateChange={val => {
                                                            setExpense(prev => ({
                                                                ...prev,
                                                                json: { ...prev.json, open_amount: val === '' ? '' : Number(val) },
                                                            }));
                                                            schedulePersist();
                                                        }}
                                                    />
                                                </div>
                                            </ExpenseFormItem>
                                        ) : null}
                                        <ExpenseFormItem data={data} label={data.t.read('mileage_type1.distance_readonly')} show={!isMileageOpenAmount()}>
                                            <div data-testid="mileage-dual-travel-distance">
                                                <FormTextField
                                                    format="distance"
                                                    locked
                                                    value={
                                                        expense.json?.Distance != null && expense.json?.Distance !== ''
                                                            ? String(expense.json.Distance)
                                                            : ''
                                                    }
                                                    validateChange={() => {}}
                                                />
                                            </div>
                                        </ExpenseFormItem>
                                    </>
                            ) : null}
                            <ExpenseFormItem data={data} label={data.t.read("round_trip")} show={expense.json?.type === 'mileage' && !isMileageOpenAmount()}>
                                <Switch className='switch invoice-value'
                                    checked={expense.json?.isRoundTrip ?? false}
                                    disabled={fieldsLocked || validating}
                                    width={70}
                                    onColor="#F39800"
                                    onHandleColor="#2693e6"
                                    boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                    activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                    onChange={val => {
                                        if (saving || validating) return;
                                        setExpense((prev) => {
                                            const j = { ...prev.json, isRoundTrip: val };
                                            clearMileageCalculatedAmounts(j);
                                            return { ...prev, json: j };
                                        });
                                        if (hasCompleteMileageRoute(expense.json)) {
                                            setSaving(true);
                                        } else {
                                            schedulePersist();
                                        }
                                    }}
                                />
                            </ExpenseFormItem>

                            <ExpenseFormItem data={data} label={`${data.t.read("rate")} / km`} show={expense.json?.type === 'mileage' && !isMileageOpenAmount()}>
                                {/* Rate is admin-only — never editable by the user */}
                                <FormTextField data-testid="expense-rate-field" format="money" locked={true} value={expense.json?.rate ?? null} />
                            </ExpenseFormItem>

                            <ExpenseFormItem data={data} label={data.t.read("invoice.billing_date")} show={showStatus(expense.json?.InvoiceDate) && expense.json?.type !== 'mileage'}>
                                <FormTextField format="date" locked={fieldsLocked} value={expense.json?.InvoiceDate ? expense.json?.InvoiceDate : ""} validateChange={val => { setExpense(prev => ({ ...prev, json: { ...prev.json, InvoiceDate: val } })); schedulePersist() }} />
                            </ExpenseFormItem>
                            <ExpenseFormItem data={data} label={data.t.read("invoice.invoice_number")} show={showStatus(expense.json?.InvoiceNo) && expense.json?.type !== 'mileage' && !simpleView}>
                                <FormTextField format="data" locked={fieldsLocked} value={expense.json?.InvoiceNo} validateChange={val => { setExpense(prev => ({ ...prev, json: { ...prev.json, InvoiceNo: val } })); schedulePersist() }} />
                            </ExpenseFormItem>
                            <ExpenseFormItem data={data} label={data.t.read("invoice.currency")} show={showStatus(expense.json?.Currency) && expense.json?.type !== 'mileage' && !simpleView}>
                                <CurrencySelect
                                    data={data}
                                    disabled={fieldsLocked}
                                    value={expense.json?.Currency}
                                    onChange={code => { setExpense(prev => ({ ...prev, json: { ...prev.json, Currency: code } })); schedulePersist() }}
                                />
                            </ExpenseFormItem>

                            {Utils.hasExpenseField(data, 'field_expense_type', false) && (
                                <ExpenseFormItem data={data} label={data.t.read("review_expense.expense_type.label")} show>
                                    <Select
                                        inputId="expense-editor-expense-type"
                                        name="expense-editor-expense-type"
                                        value={
                                            expense.json?.ExpenseType
                                                ? {
                                                    value: expense.json.ExpenseType,
                                                    label: data.t.read(`review_expense.expense_type.${expense.json.ExpenseType}`) || expense.json.ExpenseType,
                                                }
                                                : null
                                        }
                                        options={[
                                            { value: 'client', label: data.t.read('review_expense.expense_type.client') },
                                            { value: 'internal', label: data.t.read('review_expense.expense_type.internal') },
                                        ]}
                                        placeholder={data.t.read('review_expense.expense_type.placeholder')}
                                        onChange={updateExpenseType}
                                        styles={styleSelect}
                                        className="pl-1"
                                        classNamePrefix="select"
                                        isClearable
                                        isDisabled={fieldsLocked}
                                    />
                                </ExpenseFormItem>
                            )}

                            <ExpenseFormItem data={data} label={data.t.read("category")} show={showStatus(expense.json?.Category) && expense.json?.type !== 'mileage'}>
                                <Select
                                    name="Category"
                                    value={categoryValue}
                                    options={categoryOptions}
                                    formatOptionLabel={formatDeletedImputationLabel}
                                    styles={styleSelect}
                                    className="pl-1"
                                    classNamePrefix="select"
                                    onChange={updateCategory}
                                    placeholder={'Category'}
                                />
                            </ExpenseFormItem>
                            <ExpenseFormItem data={data} label={data.t.read("review_expense.imputation_subcategory.label")} show={subcategoryValue !== null || (categoryEdited && subcategoryOptions.length > 0)}>
                                <Select
                                    name="Subcategory"
                                    value={subcategoryValue}
                                    options={subcategoryOptions}
                                    formatOptionLabel={formatDeletedImputationLabel}
                                    styles={styleSelect}
                                    className="pl-1"
                                    classNamePrefix="select"
                                    onChange={updateSubcategory}
                                    placeholder={data.t.read("review_expense.imputation_subcategory.label")}
                                    isClearable
                                />
                            </ExpenseFormItem>
                            {/* XPEN-2509 option B: mandatory description for dual billable mileage is always ListItem[0].Description (shortcut below syncs it when simplified). */}
                            <ExpenseFormItem data={data} label={data.t.read("invoice.description")} show={simpleView} requiredMark={mileageType2Required && simpleView}>
                                <FormTextField format="data" locked={fieldsLocked} value={expense.json?.ListItem?.length === 1 ? expense.json?.ListItem[0]?.Description : ""} validateChange={val => {
                                    if (expense.json.ListItem?.length === 1) {
                                        updateItem({ ...expense.json.ListItem[0], Description: val }, true)
                                    } else {
                                        setExpense(prev => ({
                                            ...prev,
                                            json: { ...prev.json, Description: val }
                                        }));
                                        schedulePersist()
                                    }

                                }} />
                            </ExpenseFormItem>
                            <ExpenseFormItem data={data} label={data.t.read("invoice.is_billable")} show={expense.json?.ExpenseType !== 'internal' && (!hideBillableAndMatterForDualInterOffice() || (expense.json.type === 'mileage' && expense.json.travel_type_id))}>
                                <div className="d-flex py-2" data-testid="mileage-is-billable-field">
                                    <Checkbox disabled={fieldsLocked || isDualTravelBillableMileage} isChecked={isDualTravelBillableMileage ? true : expense.json?.IsBillable} onChange={val => {
                                        const nextListItems = (expense.json?.ListItem || []).map(item => {
                                            const withBillable = { ...item, IsBillable: val ? 1 : 0 };
                                            return recodeLineImputation(withBillable);
                                        });
                                        setExpense(prev => ({
                                            ...prev,
                                            json: {
                                                ...prev.json,
                                                IsBillable: val,
                                                ListItem: nextListItems,
                                            },
                                        }));
                                        schedulePersist();
                                    }} />
                                </div>

                            </ExpenseFormItem>
                            <ExpenseFormItem data={data} label={data.t.read("invoice.matter_id")} show={expense.json?.ExpenseType !== 'internal' && (expense.json?.type !== 'mileage' || travelTypeNeedsMatter) && (simpleView || travelTypeNeedsMatter)} requiredMark={mileageMatterRequired && (simpleView || travelTypeNeedsMatter)}>
                                <div data-testid="mileage-matter-id-field">
                                <AutoCompleteField
                                    value={resolveMatterIdFromJson(expense.json) ?? ""}
                                    onChange={val => { }}
                                    onParentBlur={(val) => {
                                        let listItems = expense.json?.ListItem;
                                        listItems.forEach(item => {
                                            if ([1, true].includes(item.IsBillable)) {
                                                item.MatterId = val
                                            }
                                        })
                                        setExpense(prev => ({ ...prev, json: { ...prev.json, MatterId: val, ListItem: listItems } })); schedulePersist()
                                    }
                                    }
                                    className="form-control pl-0 border-0"
                                    searchUrl="/matters/search/"
                                    // XPEN-3322: the picker edits the collapsed number; the full one is
                                    // restored before it is stored, so MatterId still resolves at export.
                                    formatEditValue={formatMatterNumber}
                                    parseEditValue={expandMatterNumber}
                                    minLength={1}
                                    disabled={fieldsLocked}
                                    placeholder={data.t.read("invoice.matter_id")}
                                    formatLabel={(m) => formatMatterLabelFromData(data, m)}
                                />
                                </div>
                            </ExpenseFormItem>

                            {/*Work Date*/}
                            <ExpenseFormItem data={data} label={data.t.read("invoice.work_date")} show={!data.config.frontend_invoice.hide_work_date && showStatus(expense.json?.ListItem?.length === 1 ? expense.json?.ListItem[0]?.WorkDate : expense.json?.WorkDate)}>
                                <FormTextField format="date" locked={fieldsLocked} value={expense.json?.ListItem?.length === 1 ? expense.json?.ListItem[0]?.WorkDate : ""} validateChange={val => {
                                    if (expense.json.ListItem?.length === 1) {
                                        updateItem({ ...expense.json.ListItem[0], WorkDate: val }, true)
                                    } else {
                                        setExpense(prev => ({
                                            ...prev,
                                            json: { ...prev.json, WorkDate: val }
                                        }));
                                        schedulePersist()
                                    }
                                }} />
                            </ExpenseFormItem>

                            {readOnly && (
                                <div className="alert alert-info" role="status" data-testid="expense-read-only">
                                    {data.t.read('report_edit_lock.read_only')}
                                </div>
                            )}
                            {amountLock && (
                                <div className="alert alert-info" role="status" data-testid="expense-amount-lock">
                                    {data.t.read(`expense_amount_correction.${amountLock}`)}
                                </div>
                            )}
                            <ExpenseFormItem data={data} label={data.t.read("invoice.total")}
                                show={showStatus(expense.json?.Total) && expense.json?.type !== 'mileage' && !simpleView}>
                                <FormTextField format="money" locked={fieldsLocked || !!amountLock}
                                    value={expense.json?.Total ? expense.json?.Total : ""}
                                    validateChange={val => {
                                        if (expense.json.ListItem?.length === 1) {
                                            updateItem({ ...expense.json.ListItem[0], Total: val }, true)
                                        } else {
                                            setExpense(prev => ({
                                                ...prev,
                                                json: { ...prev.json, Total: val }
                                            }));
                                            schedulePersist()
                                        }

                                    }} /> </ExpenseFormItem>
                            <ExpenseFormItem data={data} label={data.t.read("invoice.tax")} show={showStatus(expense.json?.Tax) && expense.json?.type !== 'mileage' && !unadjusted_total_view}>
                                <FormTextField format="money" locked={true} value={expense.json?.Tax ? expense.json?.Tax : ""} validateChange={val => { setExpense(prev => ({ ...prev, json: { ...prev.json, Tax: val } })); schedulePersist() }} />
                            </ExpenseFormItem>
                            <ExpenseFormItem data={data} label={data.t.read("invoice.total_with_tax")} show={showStatus(expense.json?.TotalWithTax) && expense.json?.type !== 'mileage' && !unadjusted_total_view}>
                                <FormTextField format="money" locked={true} value={expense.json?.TotalWithTax ? expense.json?.TotalWithTax : ""} validateChange={val => { setExpense(prev => ({ ...prev, json: { ...prev.json, TotalWithTax: val } })); schedulePersist() }} />
                            </ExpenseFormItem>
                            <ExpenseFormItem data={data} label={data.t.read("tips")} show={showStatus(expense.json?.Tip) && expense.json?.type !== 'mileage'}>
                                <FormTextField format="money" locked={fieldsLocked || !!amountLock} value={expense.json?.Tip ? expense.json?.Tip : ""} validateChange={val => handleTipChange(val)} />
                            </ExpenseFormItem>
                            <ExpenseFormItem data={data} label={data.t.read("invoice.total")} show={showStatus(expense.json?.BigTotalPayed) && !(simpleView && unadjusted_total_view)}>
                                <FormTextField format="money" locked={true} value={expense.json?.BigTotalPayed ? expense.json?.BigTotalPayed : ""} validateChange={val => { setExpense(prev => ({ ...prev, json: { ...prev.json, BigTotalPayed: val } })); schedulePersist() }} />
                            </ExpenseFormItem>

                            {/*Unadjusted Total*/}
                            <ExpenseFormItem data={data} label={data.t.read("invoice.unadjusted_total")} show={expense.json?.type !== 'mileage' && showStatus(expense.json?.UnadjustedTotal) && ((!simpleView) || (simpleView && unadjusted_total_view))}>
                                <FormTextField format="money" locked={fieldsLocked || !!amountLock} value={expense.json?.UnadjustedTotal ? expense.json?.UnadjustedTotal : ""} validateChange={val => handleUnadjustedTotalChange(val)} />
                            </ExpenseFormItem>

                            {/*Tax Code*/}
                            <ExpenseFormItem data={data} label={data.t.read("tax_code")} show={expense.json?.type !== 'mileage' && showStatus(expense.json?.UnadjustedTotal) && ((simpleView) || (simpleView && unadjusted_total_view))}>
                                <TaxCodeSelector
                                    taxCodes={taxeCodes}
                                    value={expense.json?.TaxCode ?? defaultTaxCode}
                                    disabled={fieldsLocked}
                                    onChange={(taxCode) => handleExpenseTaxCodeChange(expense, taxCode)}
                                />
                            </ExpenseFormItem>

                            {shouldShowHeaderGlUnitField(data) && (
                                <ExpenseFormItem
                                    data={data}
                                    label={data.t.read('invoice.unit')}
                                    show={showStatus(expense.json?.GLUnit) && expense.json?.type !== 'mileage' && !simpleView}
                                >
                                    <FormAsyncSelect
                                        data-testid="expense-header-gl-unit"
                                        searchUrl="/suppliers/unit/search"
                                        placeholder={data.t.read('invoice.unit')}
                                        value={expense.json?.GLUnit ?? null}
                                        onChange={(val) => {
                                            setExpense(prev => ({
                                                ...prev,
                                                json: { ...prev.json, GLUnit: val?.value },
                                            }));
                                            schedulePersist();
                                        }}
                                        isDisabled={fieldsLocked}
                                    />
                                </ExpenseFormItem>
                            )}

                            <div className="mt-5"
                                hidden={data.config?.frontend_invoice?.hide_cost_details_table || simpleView}>
                                <h4 className="hidden-actions-container d-flex justify-content-between align-items-center">
                                    <span className={data.isMobile ? "flex-1 d-flex justify-content-between align-items-center" : ""}>
                                        {getLineItemTableTitle(data, 'cost')}
                                        <span className="flex-1 text-right">
                                            <button
                                                disabled={fieldsLocked || isInternalExpenseType}
                                                title={isInternalExpenseType ? data.t.read('error_non_client_billable_not_allowed') : undefined}
                                                className={`btn ${data.isMobile ? "btn-secondary mr-2" : "btn btn-link"}`}
                                                onClick={() => addItem(true)}
                                            >
                                                {data.isMobile
                                                    ? data.t.read("add")
                                                    : <FontAwesomeIcon className="text-success" icon={faPlusSquare} />
                                                }
                                            </button>
                                        </span>
                                    </span>
                                    <div className="text-right">
                                        <button className={"btn btn-secondary" + (billablesSelected?.length > 0 ? "" : " faded") + (data.isMobile ? '' : " btn-sm btn-xs")} onClick={() => confirmDeleteAction("billable")} >{data.t.read("delete")}</button>
                                    </div>
                                </h4>
                                {hasDisbursementItems() &&
                                    <table className={data.isMobile ? "table invoice-items-table mt-3 font-xsmall" : "table invoice-items-table mt-3 font-xsmall"}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '5%', maxWidth: '35px' }} >
                                                    <Checkbox disabled={fieldsLocked} isChecked={(expense.json?.ListItem || []).filter(item => itemUsesDisbursementCode(item))?.length === billablesSelected?.length && billablesSelected?.length > 0} onChange={e => toggleSelectAll("billable")} />
                                                </th>
                                                <th className="col-3" >Description</th>
                                                <th className="col-2" hidden={data.isMobile || !Utils.isEngineType(data, "legal")} >{data.t.read("office")}</th>
                                                <th className="col-2" hidden={data.isMobile || data.config.frontend_invoice.hide_work_date} >{data.t.read("invoice.work_date")}</th>
                                                <th className="col-3" hidden={data.isMobile}>{data.t.read("invoice.matter_id")}</th>
                                                <th className="col-2" hidden={data.isMobile} >{data.t.read("invoice.cost_type")}</th>
                                                <th className="col-2" hidden={data.isMobile} >{data.t.read("invoice.tax_code")}</th>
                                                <th className="col-2" >{data.t.read("invoice.amount")}</th>
                                                <th style={{ width: '4%' }} hidden={data.isMobile}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                (expense.json?.ListItem || []).filter(item => itemUsesDisbursementCode(item)).map((item) => {
                                                    return <tr key={"invoice-item-" + item._view_id}>
                                                        <td onClick={e => e.stopPropagation()} className="align-middle" >
                                                            <Checkbox isChecked={billablesSelected.findIndex(it => it._view_id === item._view_id) > -1} onChange={e => toggleItemSelected("billable", item)} />
                                                        </td>
                                                        <td className="align-middle">
                                                            <FormTextField format="textarea-div" locked={fieldsLocked} value={item.Description} validateChange={val => updateItem({ ...item, Description: val })} />
                                                        </td>

                                                        <td className="align-middle" hidden={data.isMobile || !Utils.isEngineType(data, "legal")} >
                                                            <AsyncSelect
                                                                name="office"
                                                                value={{ label: item.Office, value: item.Office }}
                                                                defaultOptions cacheOptions loadOptions={loadOffices}
                                                                className={`border-0`}
                                                                styles={Utils.customStyleSelect}
                                                                classNamePrefix="select"
                                                                components={{ DropdownIndicator: null, IndicatorSeparator: null }}
                                                                onChange={(choice) => updateItem({ ...item, Office: choice.value })}
                                                                placeholder={data.t.read("office")} />
                                                        </td>
                                                        <td className="align-middle" hidden={data.isMobile || data.config.frontend_invoice.hide_work_date} >
                                                            <FormTextField format="date" locked={fieldsLocked} value={item.WorkDate ?? ""} validateChange={val => updateItem({ ...item, WorkDate: val })} />
                                                        </td>

                                                        <td className="align-middle" hidden={data.isMobile}>
                                                            <div className="d-flex align-items-center">
                                                                <AutoCompleteField
                                                                    value={item.MatterId ?? ""}
                                                                    onChange={val => updateItem({ ...item, MatterId: val.name ?? val })}
                                                                    className="form-control border-0 pl-0"
                                                                    searchUrl="/matters/search/"
                                                                    // XPEN-3322: the picker edits the collapsed number; the full one is
                                                                    // restored before it is stored, so MatterId still resolves at export.
                                                                    formatEditValue={formatMatterNumber}
                                                                    parseEditValue={expandMatterNumber}
                                                                    minLength={1}
                                                                    placeholder={data.t.read("invoice.matter_id")}
                                                                    formatLabel={(m) => formatMatterLabelFromData(data, m)} />
                                                                {shouldShowActivityInlineTrigger(data) && (
                                                                <div onClick={() => { setShowAdditionalFieldsModal(true); setWorkItem(item); setAdditionalFieldsFilter(buildActivityAdditionalFieldsFilter(data)); }} >
                                                                    <HoverInfoCard
                                                                        info={<FontAwesomeIcon icon={faExclamationTriangle} className='text-primary pointer' size={"2x"} />}
                                                                        position="pos-top-right"
                                                                        additionalInfo={
                                                                            <>
                                                                                <div style={{ minWidth: "200px", textTransform: "none" }}>
                                                                                    <strong className="mr-2">{data.t.read('validation_error')} </strong>
                                                                                    <strong>{data.t.read('invoice.activity')}</strong>
                                                                                    <div>
                                                                                        <p className="mb-0 mt-2 italic">
                                                                                            {data.t.read('click_to')} {data.t.read('select')} {data.t.read('invoice.activity')}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </>
                                                                        }
                                                                        shouldDisplayIcon={(callback) => {
                                                                            getMatterInfo(item.MatterId, (matterInfo) => {
                                                                                if (!matterInfo) return callback(false);
                                                                                callback(getActivityMissingKeys(item, matterInfo).length > 0);
                                                                            });
                                                                        }}
                                                                    />
                                                                </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="align-middle" hidden={data.isMobile}>
                                                            <AutoCompleteField
                                                                value={item.CostType}
                                                                onChange={val => updateItem({ ...item, CostType: val.name ?? val })}
                                                                className="form-control border-0 pl-0"
                                                                searchUrl="/cost_type/search/"
                                                                minLength={1}
                                                                placeholder={data.t.read("invoice.cost_type")} />
                                                        </td>
                                                        <td className="align-middle" hidden={data.isMobile}>
                                                            <TaxCodeSelector
                                                                taxCodes={taxeCodes}
                                                                value={item.TaxCode ?? defaultTaxCode}
                                                                disabled={fieldsLocked}
                                                                onChange={(taxCode) => updateItem({ ...item, TaxCode: taxCode }, true)}
                                                            />
                                                        </td>

                                                        <td>
                                                            <FormTextField format="money" locked={fieldsLocked || !!amountLock} value={item.Total} validateChange={val => updateItem({ ...item, Total: val }, true)} />
                                                        </td>
                                                        <td hidden={data.isMobile}>
                                                            <div className="d-flex flex-column align-items-center p-0 m-0">
                                                                <FontAwesomeIcon
                                                                    icon={faEllipsisH}
                                                                    className="pointer"
                                                                    data-testid={`expense-line-additional-fields-${item._view_id}`}
                                                                    onClick={() => openAdditionalFieldsForLine(item, 'billable')}
                                                                />
                                                                <FontAwesomeIcon
                                                                    className="text-danger mt-2 pointer"
                                                                    icon={faWindowClose}
                                                                    onClick={() => setConfirmDeleteViewId(item._view_id)}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                })
                                            }
                                        </tbody>
                                    </table>}
                            </div>

                            <div className="mt-5" hidden={simpleView}>
                                <h4 className="hidden-actions-container d-flex justify-content-between align-items-center">
                                    <span className={data.isMobile ? "flex-1 d-flex justify-content-between align-items-center" : ""}>
                                        {getLineItemTableTitle(data, 'gl')}
                                        <span className="flex-1 text-right">
                                            <button disabled={fieldsLocked} className={`btn ${data.isMobile ? "btn-secondary mr-2" : "btn btn-link"}`} onClick={() => addItem(false)}>
                                                {data.isMobile
                                                    ? data.t.read("add")
                                                    : <FontAwesomeIcon className="text-success" icon={faPlusSquare} />
                                                }
                                            </button>
                                        </span>
                                    </span>
                                    <div className="text-right">
                                        <button className={"btn btn-secondary " + (nonBillablesSelected?.length > 0 ? "" : "faded") + (data.isMobile ? '' : " btn-sm btn-xs")} onClick={() => confirmDeleteAction("non-billable")} >{data.t.read("delete")}</button>
                                    </div>
                                </h4>
                                {hasGlItems() &&
                                    <table className={data.isMobile ? "table invoice-items-table mt-3 font-xsmall" : "table invoice-items-table mt-3 font-xsmall"}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '5%' }}>
                                                    <Checkbox disabled={fieldsLocked} isChecked={(expense.json?.ListItem || []).filter(item => !itemUsesDisbursementCode(item))?.length === nonBillablesSelected?.length && nonBillablesSelected?.length > 0} onChange={e => toggleSelectAll("non-billable")} />
                                                </th>
                                                <th className="col-3" >Description</th>
                                                <th className="col-2" hidden={data.isMobile || !showLineGlUnitColumn}>{data.t.read('invoice.unit')}</th>
                                                <th className="col-2" hidden={data.isMobile || !Utils.isEngineType(data, "legal")} >{data.t.read(shouldUseInvoiceOfficeLabel(data) ? "invoice.office" : "office")}</th>
                                                <th hidden={data.isMobile} className="col-3">{data.t.read("invoice.gl_nat")}</th>
                                                <th className="col-3" hidden={data.isMobile}>{data.t.read("invoice.tax_code")}</th>
                                                <th className={data.isMobile ? "col-2" : "col-3"}>{data.t.read("invoice.amount")}</th>
                                                <th style={{ width: '4%' }} hidden={data.isMobile}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                (expense.json?.ListItem || []).filter(item => !itemUsesDisbursementCode(item)).map((item) => {
                                                    return <tr key={"invoice-item-" + item._view_id}>
                                                        <td onClick={e => e.stopPropagation()} className="align-middle" >
                                                            <Checkbox isChecked={nonBillablesSelected.findIndex(it => it._view_id === item._view_id) > -1} onChange={e => toggleItemSelected("non-billable", item)} />
                                                        </td>
                                                        <td className="align-middle" >
                                                            <FormTextField format="textarea-div" locked={fieldsLocked} value={item.Description} validateChange={val => updateItem({ ...item, Description: val })} />
                                                        </td>
                                                        <td className="align-middle" hidden={data.isMobile || !showLineGlUnitColumn}>
                                                            <FormAsyncSelect
                                                                data-testid={`expense-line-gl-unit-${item._view_id}`}
                                                                searchUrl="/suppliers/unit/search"
                                                                placeholder={data.t.read('invoice.unit')}
                                                                value={item.GLUnit ?? null}
                                                                optionMap={(option) => {
                                                                    const name = option.name ?? "";
                                                                    const description = option.description ?? "";
                                                                    return {
                                                                        value: { name, unit: option.unit },
                                                                        label: option.label ?? `${name}${description ? ` (${description})` : ""}`,
                                                                        name,
                                                                        description,
                                                                    };
                                                                }}
                                                                onChange={(val) => {
                                                                    updateItem({
                                                                        ...item,
                                                                        GLUnit: val?.value?.name,
                                                                        GLOffice: data?.config?.unit_is_office === true
                                                                            ? val?.value?.unit
                                                                            : item.GLOffice,
                                                                    });
                                                                }}
                                                                isDisabled={fieldsLocked}
                                                            />
                                                        </td>
                                                        <td className="align-middle" hidden={data.isMobile || !Utils.isEngineType(data, "legal")} >
                                                            {Utils.isExportFormat(data, "elite") ?
                                                                <AutoCompleteField
                                                                    value={item.GLOffice ?? ""}
                                                                    onChange={(val) => updateItem({ ...item, GLOffice: val?.name ?? val })}
                                                                    className="form-control border-0 pl-0"
                                                                    searchUrl="/offices/search/"
                                                                    minLength={1}
                                                                    placeholder={data.t.read("invoice.office")} />
                                                            :
                                                                <AsyncSelect
                                                                    name="office"
                                                                    value={{ label: item.Office, value: item.Office }}
                                                                    defaultOptions cacheOptions loadOptions={loadOffices}
                                                                    className={`border-0`}
                                                                    styles={Utils.customStyleSelect}
                                                                    classNamePrefix="select"
                                                                    components={{ DropdownIndicator: null, IndicatorSeparator: null }}
                                                                    onChange={(choice) => updateItem({ ...item, Office: choice.value })}
                                                                    placeholder={data.t.read("office")} />
                                                                }
                                                        </td>
                                                        <td className="align-middle" hidden={data.isMobile}>
                                                            <AutoCompleteField
                                                                value={item.GLNatural ?? ""}
                                                                onChange={val => updateItem({
                                                                    ...item,
                                                                    GLNatural: val.name ?? val
                                                                })}
                                                                className="form-control border-0 pl-0"
                                                                searchUrl="/gl/nat/search/"
                                                                minLength={1}
                                                                placeholder={data.t.read("gl")} />
                                                        </td>
                                                        <td className="align-middle" hidden={data.isMobile}>
                                                            <TaxCodeSelector
                                                                taxCodes={taxeCodes}
                                                                value={item.TaxCode ?? defaultTaxCode}
                                                                disabled={fieldsLocked}
                                                                onChange={(taxCode) => updateItem({ ...item, TaxCode: taxCode }, true)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <FormTextField format="money" locked={fieldsLocked || !!amountLock} value={item.Total} validateChange={val => updateItem({ ...item, Total: val }, true)} />
                                                        </td>
                                                        <td hidden={data.isMobile}>
                                                            <div className="d-flex flex-column align-items-center p-0 m-0">
                                                                <FontAwesomeIcon
                                                                    icon={faEllipsisH}
                                                                    className="pointer"
                                                                    data-testid={`expense-line-additional-fields-${item._view_id}`}
                                                                    onClick={() => openAdditionalFieldsForLine(item, 'gl')}
                                                                />
                                                                <FontAwesomeIcon
                                                                    className="text-danger mt-2 pointer"
                                                                    icon={faWindowClose}
                                                                    onClick={() => setConfirmDeleteViewId(item._view_id)}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                })
                                            }
                                        </tbody>
                                    </table>}
                            </div>

                            <div hidden={simpleView}>
                                <h4 className={`mt-5 ${data.isMobile ? "mb-4" : ""}`}
                                    style={{ display: showStatus(expense.json?.Tax1) === "flex" || showStatus(expense.json?.Tax1) === "flex" ? "block" : "none" }}>{data.t.read("invoice.tax_details")}</h4>

                                <ExpenseFormItem data={data}
                                    label={!data.config?.tax_1_label ? `${data.t.read("invoice.tax")} 1` : data.config.tax_1_label}
                                    show={showStatus(expense.json?.Tax1) && !data.config.hide_tax_1_field}>
                                    <FormTextField format="money" locked={fieldsLocked || !!amountLock}
                                        value={expense.json?.Tax1 ? expense.json.Tax1 : ""}
                                        validateChange={val => {
                                            setExpense(prev => ({ ...prev, json: { ...prev.json, Tax1: val } }));
                                            schedulePersist()
                                        }} />
                                </ExpenseFormItem>
                                <ExpenseFormItem data={data}
                                    label={!data.config?.tax_2_label ? `${data.t.read("invoice.tax")} 2` : data.config.tax_2_label}
                                    show={showStatus(expense.json?.Tax2) && !data.config.hide_tax_2_field}>
                                    <FormTextField format="money" locked={validating || saving || !!amountLock}
                                        value={expense.json?.Tax2 ? expense.json.Tax2 : ""}
                                        validateChange={val => {
                                            setExpense(prev => ({ ...prev, json: { ...prev.json, Tax2: val } }));
                                            schedulePersist()
                                        }} />
                                </ExpenseFormItem>
                            </div>

                            {!hasExpenseCommentIdentity(expense) && <h4 className="mt-5">{data.t.read("comments")}</h4>}
                            <div className={"d-flex flex-column"}>
                                <div className={"mb-4"}>
                                    {hasExpenseCommentIdentity(expense) ? <CommentsPanel invoiceId={expense.expense_uid} subjectType="expense"
                                        refreshKey={expense.json} data={data} legacyText={legacyCommentsText(expense)} onDraftChange={onDraftChange} /> : (
                                        <FormTextAreaAutoSaved
                                        key={`expense-editor-comments-${draftRestoreGeneration}`}
                                        locked={validating || saving}
                                        value={expense.json?.comments ?? ''}
                                        validateChange={val => { setExpense(prev => ({ ...prev, json: { ...prev.json, comments: val } })); schedulePersist() }}
                                    />
                                    )}
                                </div>
                                <div style={{ visibility: expenseViolations?.length > 0 ? "visible" : "hidden", }}>
                                    {expenseViolations?.length > 0 && (
                                        <div
                                            className="alert alert-danger mt-3 mb-4"
                                            role="alert"
                                            style={{
                                                display: "inline-block",
                                                maxWidth: data.isMobile ? "100%" : "50%",
                                            }}
                                        >
                                            <strong>Rule Violations:</strong>
                                            <ul className="mb-0 mt-2">
                                                {expenseViolations.map((violation, idx) => (
                                                    <li key={idx}>{violation}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                                {showManualSaveFooter && (
                                    <>
                                        {visibleMileageValidationErrorKeys.length > 0 && (
                                            <div
                                                className="alert alert-danger errors mb-3"
                                                role="alert"
                                                data-testid="mileage-validation-alert"
                                                style={{
                                                    maxWidth: data.isMobile ? '100%' : undefined,
                                                    color: '#721c24',
                                                }}
                                            >
                                                <ul className="mb-0 pl-3" style={{ color: '#721c24' }}>
                                                    {visibleMileageValidationErrorKeys.map((key) => {
                                                        const message = data.t.read(key);
                                                        return (
                                                            <li key={key}>
                                                                {message != null && String(message).trim() !== ''
                                                                    ? message
                                                                    : key}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        )}
                                        <div
                                            className="d-flex mt-4 justify-content-between align-items-center"
                                            data-testid="expense-save-footer"
                                        >
                                            <div></div>
                                            <div className="d-flex">
                                                {isExpenseEditorDraftDirty && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-light btn-sm mr-2"
                                                        disabled={fieldsLocked || validating}
                                                        onClick={handleRestoreOriginal}
                                                        data-testid="expense-restore-original"
                                                    >
                                                        {data.t.read('restore_original')}
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary mr-2"
                                                    disabled={fieldsLocked || validating}
                                                    onClick={isNewCreation ? handleCancelCreation : close}
                                                    data-testid="expense-creation-cancel"
                                                >
                                                    {data.t.read('cancel')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    disabled={fieldsLocked || validating}
                                                    onClick={handleManualSave}
                                                    data-testid={isNewCreation ? 'expense-creation-save' : 'expense-edit-save'}
                                                >
                                                    {data.t.read('save')}
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                                {/* XPEN-2872: Restart Processing lives outside the manual-save footer so it
                                    stays visible for auto-saving receipts (footer only renders for mileage). */}
                                {/* XPEN-3233: the row renders whenever either button inside it would —
                                    mileage lines still need the Validate button even though Restart process
                                    (receipt-only, AI processing never runs on mileage) does not show for them;
                                    skip the row entirely rather than mount an empty flex row when neither
                                    button would render (mileage + not allowed to validate — XPEN-3375 swapped
                                    this condition from controller-view access to userCanValidateExpenses). */}
                                {(expense.json?.type !== 'mileage' || canValidate) && (
                                <div className="d-flex mt-4" data-testid="expense-restart-footer">
                                    {expense.json?.type !== 'mileage' && (
                                        <button
                                            type="button"
                                            className={`btn btn-secondary ${data.isMobile ? '' : 'btn-sm'}`}
                                            disabled={fieldsLocked || validating}
                                            onClick={() => setShowConfirmRestartProcess(true)}
                                            data-testid="expense-restart-process"
                                        >
                                            {data.t.read('invoice.restart_processing')}
                                        </button>
                                    )}
                                    {canValidate && (
                                        <button
                                            type="button"
                                            className="btn btn-primary mx-auto"
                                            disabled={fieldsLocked || validating || !canValidateExpense(expense.status)}
                                            onClick={() => validateExpense()}
                                            data-testid="expense-validate"
                                        >
                                            {data.t.read('validate')}
                                        </button>
                                    )}
                                </div>
                                )}
                            </div>
                        </div>

                    </div>
                    </div>
                </div>
            }
            {
                showConfirmRestartProcess === true &&
                <Modal title={data.t.read("confirm")} cancelAction={() => setShowConfirmRestartProcess(false)} okAction={() => restartProcess()} okText={data.t.read("yes")} cancelText={data.t.read("no")}>
                    {data.t.read("invoice.restart_processing")} ?
                </Modal>

            }
            {
                showConfirmRestartPostProcess === true &&
                <Modal title={data.t.read("confirm")} cancelAction={() => setShowConfirmRestartPostProcess(false)} okAction={() => restartPostProcess()} okText={data.t.read("yes")} cancelText={data.t.read("no")}>
                    {data.t.read("invoice.restart_post_processing")} ?
                </Modal>

            }
            {
                confirmDeleteItems != null &&
                <Modal title={data.t.read("confirm")} cancelAction={() => setConfirmDeleteItems()} okAction={() => deleteItems()} okText={data.t.read("delete")} cancelText={data.t.read("cancel")}>
                    {data.t.read("delete")} ?
                </Modal>

            }
            {
                confirmDeleteViewId != null &&
                <Modal title={data.t.read("confirm")} cancelAction={() => setConfirmDeleteViewId(null)} okAction={() => deleteItem(confirmDeleteViewId)} okText={data.t.read("delete")} cancelText={data.t.read("cancel")}>
                    {data.t.read("delete")} ?
                </Modal>

            }
            {
                savingError != null &&
                <Modal title={data.t.read("error")} okAction={() => setSavingError(null)} okText={"ok"}>
                    <div className="text-danger" role="alert">
                        {savingError}
                    </div>
                </Modal>

            }
            {
                showDisapproval &&
                <Modal overflow="visible" title={data.t.read("denial_cause_placeholder")} cancelAction={() => setShowDisapproval(false)} okAction={() => disApproveExpense()} okText={data.t.read("submit")} cancelText={data.t.read("cancel")}>
                    <input className='form-control' type="text" onChange={e => setCauseDenial(e.target.value)} />
                </Modal>
            }
            {
                showAdditionalFieldsModal &&
                <Modal
                    title={data.t.read("invoice.additional_fields")}
                    width={400}
                    centered
                    onClose={() => { setShowAdditionalFieldsModal(false); setAdditionalFieldsFilter([]); }}
                    secondaryAction={() => { setShowAdditionalFieldsModal(false); setAdditionalFieldsFilter([]); }}
                    secondaryText={data.t.read("close")}
                >
                    <AdditionalFieldsModal
                        onClose={() => { setShowAdditionalFieldsModal(false); setAdditionalFieldsFilter([]); }}
                        workItem={workItem}
                        saving={saving}
                        locked={false}
                        updateInvoice={updateItemForModal}
                        activityMap={activityMap}
                        fields={[...additionalFieldsFilter]}
                    />
                </Modal>
            }
        </div>


}

export default observer(Expense);
